import { buildUserTranscriptEntries } from './voiceUserTranscripts';
import type { UserTranscriptTurn } from './voiceUserTranscripts';
import { reconcileAssistantTranscriptEntries } from './voiceAssistantTranscripts';
import type { AssistantTranscriptEntry } from './voiceAssistantTranscripts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Mic,
  MicOff,
  RefreshCw,
  Settings,
  Volume2,
  Waves,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useEventTracking } from '../../hooks/useEventTracking';
import type { QuestionFlowProps } from './types';
import { QuestionLayout } from '../layout/QuestionLayout';
import { useFlowThemeTransitionReady } from './themes/FlowThemeTransition';
import { QuestionFlowLoadingState } from './QuestionFlowLoadingState';
import { useVoiceSession, UseVoiceSessionParams, UseVoiceSessionResult } from './useVoiceSession';
import { useLiveVoiceSession } from './useLiveVoiceSession';
import { useHoldPullToRefresh } from 'mobile/pullToRefreshHold';
import { useVoiceRoleplayHost, VoiceRoleplayDraft } from './useVoiceRoleplayHost';
import { buildRoleplayConversationHistory, buildRoleplayConversationMessages, getRoleplayConversationHistoryTargets, mergeRoleplayConversationMessages } from './voiceRoleplayConversation';
import type { RoleplayConversationHistoryTarget, RoleplayConversationMessage, RoleplayLiveCaption } from './voiceRoleplayConversation';
import type { RoleplaySession } from 'services/api/roleplayClient';
import VoiceProcessingModelControls from './VoiceProcessingModelControls';
import { flows, courses, quiz, roleplay as roleplayApi, user as userApi, voiceFlow as voiceFlowApi } from '../../services/api';
import type { VoiceSessionContext, VoiceFlowAiSafetyState, VoiceFlowCostEstimate, VoiceFlowLatencyMetrics, VoiceFlowSessionBootstrap, VoiceRuntime, VoiceProcessingModels, VoiceProcessingModelOptions } from '../../types';
import Popup, { PopupHandle } from '../popup';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import { buildFlowPath } from '../../utils/flowNavigation';
import { QuestionText } from 'utils/questionMarkdown';
import { AnswerReferenceContext } from 'utils/answerReferences';
import { FlowThemeLayer, ThemeContextProvider, type FlowThemeProgress } from './themes';
import { useLearnerThemeContext } from './themes/useLearnerThemeContext';
import { createThemeCompletionId } from './themes/themeCompletionEvents';
import type { Question, ResponsePayload } from '../../types';
import './VoiceFlow.css';

type VoiceRuntimeStatus =
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'muted'
  | 'reconnecting'
  | 'disconnected'
  | 'error'
  | 'permission'
  | 'paused'
  | 'idle_paused'
  | 'done';

type TranscriptEntry = {
  id: string;
  text: string;
  timestamp: string;
  isLive?: boolean;
};

type AssistantEntry = AssistantTranscriptEntry;

type ConversationMessage = {
  id: string;
  role: 'guide' | 'user' | 'actor';
  speaker?: string;
  text: string;
  timestamp: string;
  isLive?: boolean;
  isStreaming?: boolean;
  order: number;
};

type VoiceDebugConfirmAction = 'clear_crisis' | 'reset_run';
type VoiceDebugActionBusy = VoiceDebugConfirmAction | 'realtime_model' | 'transcription_model' | 'runtime' | 'processing_models';
type VoiceDebugSectionId =
  | 'staffActions'
  | 'runtime'
  | 'processingModels'
  | 'realtimeModel'
  | 'transcriptionModel'
  | 'liveTranscripts'
  | 'assistantText'
  | 'extractionResult'
  | 'questionsAnswers'
  | 'realtimeEvents'
  | 'currentInstructions';

const DEBUG_DRAWER_DEFAULT_WIDTH = 420;
const DEBUG_DRAWER_MIN_WIDTH = 360;
const DEBUG_DRAWER_MAX_VIEWPORT_RATIO = 0.7;
const DEBUG_SECTION_STORAGE_KEY = 'wisefriend.voiceDebugDrawer.sections.v1';
const DEBUG_DRAWER_WIDTH_STORAGE_KEY = 'wisefriend.voiceDebugDrawer.width.v1';

const DEFAULT_DEBUG_SECTION_STATE: Record<VoiceDebugSectionId, boolean> = {
  staffActions: true,
  runtime: true,
  processingModels: true,
  realtimeModel: true,
  transcriptionModel: true,
  liveTranscripts: true,
  assistantText: true,
  extractionResult: true,
  questionsAnswers: true,
  realtimeEvents: true,
  currentInstructions: false,
};

const isVoiceDebugSectionId = (value: string): value is VoiceDebugSectionId => (
  Object.prototype.hasOwnProperty.call(DEFAULT_DEBUG_SECTION_STATE, value)
);

const loadDebugSectionState = (): Record<VoiceDebugSectionId, boolean> => {
  if (typeof window === 'undefined') {
    return DEFAULT_DEBUG_SECTION_STATE;
  }

  try {
    const rawValue = window.localStorage.getItem(DEBUG_SECTION_STORAGE_KEY);
    if (!rawValue) {
      return DEFAULT_DEBUG_SECTION_STATE;
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object') {
      return DEFAULT_DEBUG_SECTION_STATE;
    }

    return Object.entries(parsed).reduce<Record<VoiceDebugSectionId, boolean>>(
      (sectionState, [key, value]) => {
        if (isVoiceDebugSectionId(key) && typeof value === 'boolean') {
          sectionState[key] = value;
        }
        return sectionState;
      },
      { ...DEFAULT_DEBUG_SECTION_STATE },
    );
  } catch {
    return DEFAULT_DEBUG_SECTION_STATE;
  }
};

const saveDebugSectionState = (sectionState: Record<VoiceDebugSectionId, boolean>) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(DEBUG_SECTION_STORAGE_KEY, JSON.stringify(sectionState));
  } catch {
    // localStorage can be unavailable in private browsing or locked-down test environments.
  }
};

const DEBUG_CONFIRM_COPY: Record<VoiceDebugConfirmAction, {
  title: string;
  description: string;
  confirmLabel: string;
}> = {
  clear_crisis: {
    title: 'Clear crisis pause?',
    description: 'This reactivates the current voice session for testing.',
    confirmLabel: 'Clear pause',
  },
  reset_run: {
    title: 'Reset this module run?',
    description: 'This deletes saved answers and voice draft state for this run. This cannot be undone.',
    confirmLabel: 'Reset run',
  },
};

const getResolvedFlowRunId = (
  initialBootstrap: QuestionFlowProps['initialBootstrap'],
  flowRunId: number | null | undefined,
) => {
  const flowRun = initialBootstrap?.flowRun;
  if (typeof flowRun?.flow_run_id === 'number') {
    return flowRun.flow_run_id;
  }
  if (typeof flowRun?.id === 'number') {
    return flowRun.id;
  }
  return flowRunId ?? null;
};

const prettyJson = (value: unknown) => JSON.stringify(value ?? null, null, 2);

const formatDebugTimestamp = (value?: string | null) => {
  if (!value) {
    return 'Pending';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return format(parsed, 'MMM d, h:mm:ss a');
};

const formatShortTimestamp = (value?: string | null) => {
  if (!value) {
    return 'Pending';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return format(parsed, 'h:mm:ss a');
};

const formatLatencyMs = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return `${Math.round(value)} ms`;
};

const formatEvaluationSpeed = (value?: string | null) => {
  if (value === 'default') return 'Standard';
  if (value === 'fast') return 'Fast';
  return value || 'n/a';
};

const formatCostUsd = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '$0.000000';
  }
  return `$${(value / 1_000_000).toFixed(6)}`;
};

const formatTokenCount = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0';
  }
  return new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value)));
};

const getTokenCountValue = (value: unknown) => (
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0
);

const TOKEN_COUNT_LABELS: Record<string, string> = {
  total_tokens: 'Total',
  input_tokens: 'Input',
  output_tokens: 'Output',
  text_input_tokens: 'Text input',
  text_cached_input_tokens: 'Text cached input',
  text_output_tokens: 'Text output',
  audio_input_tokens: 'Audio input',
  audio_cached_input_tokens: 'Audio cached input',
  audio_output_tokens: 'Audio output',
  image_input_tokens: 'Image input',
  image_cached_input_tokens: 'Image cached input',
  image_output_tokens: 'Image output',
};

const TOKEN_COUNT_ORDER = Object.keys(TOKEN_COUNT_LABELS);

const formatTokenCountLabel = (key: string) => (
  TOKEN_COUNT_LABELS[key]
  ?? key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
);

const getTokenCountEntries = (
  tokenCounts?: VoiceFlowCostEstimate['realtime']['token_counts'],
) => {
  if (!tokenCounts) {
    return [];
  }

  const keys = Object.keys(tokenCounts).filter((key) => typeof tokenCounts[key] === 'number');
  return keys
    .sort((left, right) => {
      const leftIndex = TOKEN_COUNT_ORDER.indexOf(left);
      const rightIndex = TOKEN_COUNT_ORDER.indexOf(right);
      if (leftIndex === -1 && rightIndex === -1) {
        return left.localeCompare(right);
      }
      if (leftIndex === -1) {
        return 1;
      }
      if (rightIndex === -1) {
        return -1;
      }
      return leftIndex - rightIndex;
    })
    .map((key) => ({
      key,
      label: formatTokenCountLabel(key),
      value: getTokenCountValue(tokenCounts[key]),
    }));
};

const getRealtimeEventPreview = (payload: Record<string, unknown>) => {
  const transcript =
    (typeof payload.transcript === 'string' && payload.transcript)
    || (typeof payload.delta === 'string' && payload.delta)
    || (typeof payload.text === 'string' && payload.text);
  if (transcript) {
    return transcript;
  }

  const preview = JSON.stringify(payload);
  if (preview.length <= 160) {
    return preview;
  }
  return `${preview.slice(0, 157)}...`;
};

const getDebugDrawerMaxWidth = () => {
  if (typeof window === 'undefined') {
    return DEBUG_DRAWER_MIN_WIDTH;
  }
  return Math.max(
    DEBUG_DRAWER_MIN_WIDTH,
    Math.min(window.innerWidth * DEBUG_DRAWER_MAX_VIEWPORT_RATIO, window.innerWidth - 32),
  );
};

const clampDebugDrawerWidth = (width: number) =>
  Math.min(getDebugDrawerMaxWidth(), Math.max(DEBUG_DRAWER_MIN_WIDTH, width));

const loadDebugDrawerWidth = () => {
  if (typeof window === 'undefined') {
    return DEBUG_DRAWER_DEFAULT_WIDTH;
  }

  try {
    const rawValue = window.localStorage.getItem(DEBUG_DRAWER_WIDTH_STORAGE_KEY);
    if (!rawValue) {
      return clampDebugDrawerWidth(DEBUG_DRAWER_DEFAULT_WIDTH);
    }

    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      return clampDebugDrawerWidth(DEBUG_DRAWER_DEFAULT_WIDTH);
    }

    return clampDebugDrawerWidth(parsed);
  } catch {
    return clampDebugDrawerWidth(DEBUG_DRAWER_DEFAULT_WIDTH);
  }
};

const saveDebugDrawerWidth = (width: number) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(DEBUG_DRAWER_WIDTH_STORAGE_KEY, String(clampDebugDrawerWidth(width)));
  } catch {
    // localStorage can be unavailable in private browsing or locked-down test environments.
  }
};

const getStatusLabel = (status: VoiceRuntimeStatus) => {
  const labels: Record<VoiceRuntimeStatus, string> = {
    connecting: 'Connecting',
    listening: 'Listening',
    thinking: 'Thinking',
    speaking: 'Speaking',
    muted: 'Muted',
    reconnecting: 'Reconnecting',
    disconnected: 'Disconnected',
    error: 'Voice mode interrupted',
    permission: 'Mic needed',
    paused: 'Paused',
    idle_paused: 'Paused',
    done: 'Complete',
  };
  return labels[status];
};

const getRuntimeStatus = ({
  sessionStatus,
  hasCurrentTarget,
  connectionState,
  micPermission,
  isMuted,
  assistantPhase,
  completionClosingPending,
  turnProcessing,
  isIdlePaused,
}: {
  sessionStatus?: string | null;
  hasCurrentTarget: boolean;
  connectionState: string;
  micPermission: string;
  isMuted: boolean;
  assistantPhase: string;
  completionClosingPending: boolean;
  turnProcessing: boolean;
  isIdlePaused: boolean;
}): VoiceRuntimeStatus => {
  if (sessionStatus === 'crisis_paused') {
    return 'paused';
  }
  if (isIdlePaused) {
    return connectionState === 'connecting' ? 'connecting' : 'idle_paused';
  }
  if (sessionStatus === 'completed' || (sessionStatus && !hasCurrentTarget)) {
    if (completionClosingPending || assistantPhase === 'responding') {
      return 'speaking';
    }
    return 'done';
  }
  if (micPermission === 'denied') {
    return 'permission';
  }
  if (connectionState === 'connecting') {
    return 'connecting';
  }
  if (connectionState === 'error') {
    return 'error';
  }
  if (connectionState === 'disconnected') {
    return 'disconnected';
  }
  if (isMuted) {
    return 'muted';
  }
  if (assistantPhase === 'responding') {
    return 'speaking';
  }
  if (turnProcessing) {
    return 'thinking';
  }
  if (connectionState === 'connected') {
    return 'listening';
  }
  return 'connecting';
};

const parseTimestamp = (value: string, fallbackOrder: number) => {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? fallbackOrder : parsed;
};

const buildConversationMessages = ({
  turnHistory,
  assistantDebugHistory,
  liveUserDebugEntry,
  liveAssistantDebugEntry,
}: {
  turnHistory: UserTranscriptTurn[];
  assistantDebugHistory: AssistantEntry[];
  liveUserDebugEntry: TranscriptEntry | null;
  liveAssistantDebugEntry: AssistantEntry | null;
}): ConversationMessage[] => {
  const messages: ConversationMessage[] = [];

  assistantDebugHistory.forEach((entry, index) => {
    if (!entry.text.trim() || (liveAssistantDebugEntry?.text.trim() && entry.id === liveAssistantDebugEntry.id)) {
      return;
    }
    messages.push({
      id: `guide-${entry.id}`,
      role: 'guide',
      text: entry.text,
      timestamp: entry.timestamp,
      order: parseTimestamp(entry.timestamp, index * 2),
    });
  });

  buildUserTranscriptEntries(turnHistory, liveUserDebugEntry).forEach((entry, index) => {
    messages.push({
      id: entry.id,
      role: 'user',
      text: entry.text,
      timestamp: entry.timestamp,
      isLive: entry.isLive,
      order: parseTimestamp(entry.timestamp, entry.isLive ? Number.MAX_SAFE_INTEGER - 2 : index * 2 + 1)
        + (entry.id.startsWith('user-capture-') ? 0 : 1),
    });
  });

  if (liveAssistantDebugEntry?.text.trim()) {
    messages.push({
      id: `guide-${liveAssistantDebugEntry.id}`,
      role: 'guide',
      text: liveAssistantDebugEntry.text,
      timestamp: liveAssistantDebugEntry.timestamp,
      isLive: true,
      isStreaming: true,
      order: parseTimestamp(liveAssistantDebugEntry.timestamp, Number.MAX_SAFE_INTEGER - 1),
    });
  }

  return messages.sort((a, b) => a.order - b.order);
};

const StatusDot: React.FC<{ status: VoiceRuntimeStatus }> = ({ status }) => {
  const kind = status === 'muted' || status === 'permission'
    ? 'mute'
    : status === 'error' || status === 'disconnected' || status === 'paused'
      ? 'error'
      : status === 'listening'
        ? 'live'
        : status === 'connecting' || status === 'thinking' || status === 'reconnecting'
          ? 'pulse'
          : 'solid';

  if (kind === 'mute') {
    return (
      <span className="vm-status-dot vm-status-dot-mute" aria-hidden="true">
        <span />
      </span>
    );
  }

  return <span className={`vm-status-dot vm-status-dot-${kind}`} aria-hidden="true" />;
};

const StatusChip: React.FC<{ status: VoiceRuntimeStatus }> = ({ status }) => (
  <span className={`vm-status-chip vm-status-${status}`}>
    <StatusDot status={status} />
    {getStatusLabel(status)}
  </span>
);

const Waveform: React.FC<{
  active?: boolean;
  bars?: number;
  className?: string;
}> = ({ active = true, bars = 14, className = '' }) => (
  <span className={`vm-waveform ${className}`} aria-hidden="true">
    {Array.from({ length: bars }).map((_, index) => (
      <span
        key={index}
        className={active ? 'vm-waveform-active' : ''}
        style={{ animationDelay: `${(index * 70) % 800}ms` }}
      />
    ))}
  </span>
);

const GuideAvatar: React.FC<{ streaming?: boolean }> = ({ streaming = false }) => (
  <span className={`vm-guide-avatar ${streaming ? 'vm-guide-avatar-speaking' : ''}`} aria-hidden="true">
    <Waveform active={streaming} bars={5} />
  </span>
);

const GuideMessage: React.FC<{
  children: React.ReactNode;
  speaker?: string;
  streaming?: boolean;
  onReplay?: () => void;
  replayDisabled?: boolean;
}> = ({ children, speaker, streaming = false, onReplay, replayDisabled = false }) => (
  <div className="vm-message-row vm-message-row-guide">
    <GuideAvatar streaming={streaming} />
    <div className="vm-guide-message-wrap">
      <div className="vm-message vm-message-guide">
        {speaker ? <span className="block text-xs font-semibold mb-1">{speaker}</span> : null}
        {children}
        {streaming ? <span className="vm-caret" aria-hidden="true" /> : null}
      </div>
      {onReplay ? (
        <button
          type="button"
          className="vm-replay-button"
          onClick={onReplay}
          disabled={replayDisabled}
          aria-label="Repeat latest guide message"
          title="Repeat latest guide message"
        >
          <Volume2 aria-hidden="true" />
        </button>
      ) : null}
    </div>
  </div>
);

const UserMessage: React.FC<{
  children: React.ReactNode;
  speaker?: string;
  live?: boolean;
}> = ({ children, speaker, live = false }) => (
  <div className="vm-message-row vm-message-row-user">
    <div className="vm-user-message-wrap">
      {speaker ? <span className="vm-live-label">{speaker}</span> : null}
      {live ? (
        <div className="vm-live-label">
          <Waveform active bars={5} />
          <span>Live</span>
        </div>
      ) : null}
      <div className={`vm-message vm-message-user ${live ? 'vm-message-user-live' : ''}`}>
        {children}
      </div>
    </div>
  </div>
);

const ThinkingRow: React.FC = () => (
  <div className="vm-thinking-row">
    <GuideAvatar streaming />
    <span className="vm-thinking-dots" aria-label="Thinking">
      <span />
      <span />
      <span />
    </span>
  </div>
);

const ConnectingSpinner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`vm-connecting-spinner ${className}`}
    aria-hidden="true"
    data-testid="voice-connecting-spinner"
  />
);

const PermissionNotice: React.FC<{ onEnable: () => void }> = ({ onEnable }) => (
  <div className="vm-centered-notice" data-testid="voice-permission-notice">
    <div className="vm-notice-icon">
      <Mic className="h-7 w-7" />
    </div>
    <h2>Microphone access is blocked</h2>
    <p>Re-enable microphone permission in your browser site settings, then try again. Your saved progress is safe.</p>
    <button type="button" className="vm-primary-action" onClick={onEnable}>
      <RefreshCw className="h-4 w-4" />
      <span>Try again</span>
    </button>
  </div>
);

const ErrorNotice: React.FC<{
  error: string | null;
  reconnecting?: boolean;
  onReconnect: () => void;
}> = ({ error, reconnecting = false, onReconnect }) => (
  <div className="vm-error-notice" data-testid="voice-error-notice">
    <div className="vm-error-icon">
      {reconnecting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
    </div>
    <div>
      <h2>{reconnecting ? 'Trying to reconnect' : 'Voice mode interrupted'}</h2>
      <p>{error || 'Your saved progress is safe. We will pick up where you left off.'}</p>
      {!reconnecting ? (
        <button type="button" className="vm-secondary-action" onClick={onReconnect}>
          <RefreshCw className="h-3.5 w-3.5" />
          <span>Reconnect</span>
        </button>
      ) : null}
    </div>
  </div>
);

const SafetyNotice: React.FC<{
  mode: 'crisis' | 'supportive_distress';
  resourcesPath: string | null;
  ctaLabel: string | null;
  onCtaClick?: () => void;
  onNarrationReplay?: () => void;
  narrationLoading?: boolean;
  narrationPlaying?: boolean;
  narrationDisabled?: boolean;
}> = ({
  mode,
  resourcesPath,
  ctaLabel,
  onCtaClick,
  onNarrationReplay,
  narrationLoading = false,
  narrationPlaying = false,
  narrationDisabled = false,
}) => (
  <div className={`vm-safety-notice vm-safety-${mode}`} data-testid="voice-safety-notice">
    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
    <div>
      <p className="font-semibold">
        {mode === 'crisis'
          ? 'Voice mode is paused so you can focus on immediate support.'
          : 'Support resources are available while you continue.'}
      </p>
      {resourcesPath && ctaLabel ? (
        <a href={resourcesPath} onClick={onCtaClick}>{ctaLabel}</a>
      ) : null}
    </div>
    {mode === 'crisis' && onNarrationReplay ? (
      <button
        type="button"
        className="vm-safety-narration-button"
        onClick={onNarrationReplay}
        disabled={narrationDisabled || narrationLoading}
        aria-label={narrationPlaying ? 'Safety message playing' : 'Read safety message aloud'}
        data-testid="voice-safety-narration-button"
      >
        {narrationLoading ? <Spinner size="sm" /> : <Volume2 className="h-4 w-4" />}
      </button>
    ) : null}
  </div>
);

const PrimaryMic: React.FC<{
  status: VoiceRuntimeStatus;
  isMuted: boolean;
  disabled: boolean;
  onMuteToggle: () => void;
  onReconnect: () => void;
}> = ({
  status,
  isMuted,
  disabled,
  onMuteToggle,
  onReconnect,
}) => {
  const isPermissionAction = status === 'permission';
  const isConnectionAction = status === 'error' || status === 'disconnected' || isPermissionAction;
  const isBusy = status === 'connecting' || status === 'reconnecting';
  const isPaused = status === 'paused';
  let label = isMuted ? 'Unmute' : 'Mute';
  let subLabel = isMuted ? 'Tap to unmute' : 'Tap to mute';
  if (isBusy) {
    label = getStatusLabel(status);
    subLabel = 'Connecting now';
  } else if (isPaused) {
    label = 'Voice paused';
    subLabel = 'Safety pause active';
  } else if (isPermissionAction) {
    label = 'Try again';
    subLabel = 'After enabling mic access';
  } else if (isConnectionAction) {
    label = 'Reconnect';
    subLabel = 'Tap to reconnect';
  } else if (status === 'thinking') {
    subLabel = 'Analyzing';
  } else if (status === 'done') {
    label = 'Conversation complete';
    subLabel = 'Saved';
  }
  const handleClick = () => {
    if (isConnectionAction) {
      void onReconnect();
      return;
    }
    onMuteToggle();
  };

  return (
    <div className="vm-primary-mic">
      <button
        type="button"
        className={`vm-primary-mic-button vm-primary-mic-${status}`}
        onClick={handleClick}
        disabled={disabled || isBusy || status === 'done'}
        aria-label={label}
      >
        {isBusy ? (
          <ConnectingSpinner />
        ) : status === 'thinking' ? (
          <span className="vm-thinking-dots vm-thinking-dots-light" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        ) : isConnectionAction ? (
          isPermissionAction ? <Mic className="h-6 w-6" /> : <RefreshCw className="h-6 w-6" />
        ) : isPaused ? (
          <AlertTriangle className="h-6 w-6" />
        ) : isMuted ? (
          <MicOff className="h-6 w-6" />
        ) : (
          <Mic className="h-6 w-6" />
        )}
      </button>
      <div>
        <p>{getStatusLabel(status)}</p>
        <span>{subLabel}</span>
      </div>
    </div>
  );
};

const VoiceControls: React.FC<{
  status: VoiceRuntimeStatus;
  onStandardMode: () => void;
  voice: string;
  personality: string;
  responseLength: string;
  followUpIntensity: string;
  voiceOptions: Array<{ id: string; label: string; description: string }>;
  personalityOptions: Array<{ id: string; label: string; description: string }>;
  responseLengthOptions: Array<{ id: string; label: string; description: string }>;
  followUpIntensityOptions: Array<{ id: string; label: string; description: string }>;
  settingsSaving: boolean;
  settingsError: string | null;
  onSaveSettings: (settings: {
    voice: string;
    personality: string;
    responseLength: string;
    followUpIntensity: string;
  }) => Promise<void>;
}> = ({
  status,
  onStandardMode,
  voice,
  personality,
  responseLength,
  followUpIntensity,
  voiceOptions,
  personalityOptions,
  responseLengthOptions,
  followUpIntensityOptions,
  settingsSaving,
  settingsError,
  onSaveSettings,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftVoice, setDraftVoice] = useState(voice);
  const [draftPersonality, setDraftPersonality] = useState(personality);
  const [draftResponseLength, setDraftResponseLength] = useState(responseLength);
  const [draftFollowUpIntensity, setDraftFollowUpIntensity] = useState(followUpIntensity);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const settingsPopupRef = useRef<PopupHandle | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!settingsOpen) {
      setDraftVoice(voice);
      setDraftPersonality(personality);
      setDraftResponseLength(responseLength);
      setDraftFollowUpIntensity(followUpIntensity);
      setPreviewError(null);
    }
  }, [followUpIntensity, personality, responseLength, settingsOpen, voice]);

  useEffect(() => () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause?.();
      previewAudioRef.current = null;
    }
  }, []);

  const hasChanges = (
    draftVoice !== voice
    || draftPersonality !== personality
    || draftResponseLength !== responseLength
    || draftFollowUpIntensity !== followUpIntensity
  );
  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause?.();
      previewAudioRef.current.currentTime = 0;
      previewAudioRef.current = null;
    }
    setPreviewingVoice(null);
  }, []);

  const handlePreviewVoice = useCallback((voiceId: string) => {
    setPreviewError(null);

    if (previewingVoice === voiceId) {
      stopPreview();
      return;
    }

    stopPreview();

    const audio = new Audio();
    audio.src = `/audio/voice-previews/${voiceId}.mp3`;
    previewAudioRef.current = audio;
    setPreviewingVoice(voiceId);

    audio.addEventListener?.('ended', () => {
      if (previewAudioRef.current === audio) {
        previewAudioRef.current = null;
        setPreviewingVoice(null);
      }
    });
    audio.addEventListener?.('error', () => {
      if (previewAudioRef.current === audio) {
        previewAudioRef.current = null;
        setPreviewingVoice(null);
        setPreviewError('Preview audio is not available for this voice yet.');
      }
    });

    if (typeof audio.play !== 'function') {
      setPreviewingVoice(null);
      setPreviewError('Preview audio could not be played by this browser.');
      return;
    }

    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {
        if (previewAudioRef.current === audio) {
          previewAudioRef.current = null;
          setPreviewingVoice(null);
          setPreviewError('Preview audio could not be played by this browser.');
        }
      });
    }
  }, [previewingVoice, stopPreview]);

  const handleSave = async () => {
    try {
      stopPreview();
      await onSaveSettings({
        voice: draftVoice,
        personality: draftPersonality,
        responseLength: draftResponseLength,
        followUpIntensity: draftFollowUpIntensity,
      });
      settingsPopupRef.current?.close();
    } catch {
      // Keep the popup open so the inline error stays visible.
    }
  };

  const handleCloseSettings = () => {
    stopPreview();
    setSettingsOpen(false);
  };

  return (
    <div className="vm-controls-wrap">
      <div className="vm-controls" data-testid="voice-top-controls">
        <StatusChip status={status} />
        <div className="vm-controls-actions">
          <div className="vm-mode-capsule" aria-label="Voice mode options" data-testid="voice-mode-capsule">
            <button
              type="button"
              className="vm-standard-button"
              onClick={onStandardMode}
              data-testid="voice-flow-standard-mode-button"
            >
              <Waves className="h-4 w-4" />
              <span>Standard mode</span>
            </button>
            <div className="vm-settings-anchor">
              <button
                type="button"
                className="vm-icon-button"
                onClick={() => setSettingsOpen((previous) => !previous)}
                aria-expanded={settingsOpen}
                aria-label="Voice settings"
                title="Voice settings"
              >
                <Settings className="h-4 w-4" />
              </button>
              {settingsOpen ? (
                <Popup
                  ref={settingsPopupRef}
                  onClose={handleCloseSettings}
                  title="Voice settings"
                  description="Choose how the voice guide sounds and responds."
                  size="wide"
                  contentLayout="form"
                  actionsLayout="end"
                  isSubmitting={settingsSaving}
                  aria-label="Voice settings"
                  style={{ pointerEvents: 'auto' }}
                  actions={(
                    <div className="vm-settings-actions">
                      <Button
                        type="button"
                        variant="secondary"
                        size="auto"
                        onClick={() => settingsPopupRef.current?.close()}
                        disabled={settingsSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="auto"
                        onClick={() => { void handleSave(); }}
                        disabled={!hasChanges || settingsSaving}
                      >
                        {settingsSaving ? (
                          <>
                            <Spinner size="sm" tone="light" />
                          </>
                        ) : 'Save'}
                      </Button>
                    </div>
                  )}
                >
                  <fieldset className="vm-settings-fieldset">
                    <legend>Voice</legend>
                    <div className="vm-settings-options">
                      {voiceOptions.map((option) => (
                        <div key={option.id} className="vm-setting-voice-row">
                          <label className={draftVoice === option.id ? 'vm-setting-option-selected' : ''}>
                            <input
                              type="radio"
                              name="voice-mode-voice"
                              value={option.id}
                              checked={draftVoice === option.id}
                              onChange={() => setDraftVoice(option.id)}
                            />
                            <span>
                              <strong>{option.label}</strong>
                              <em>{option.description}</em>
                            </span>
                          </label>
                          <button
                            type="button"
                            className={`vm-voice-preview-button ${previewingVoice === option.id ? 'vm-voice-preview-active' : ''}`}
                            onClick={() => handlePreviewVoice(option.id)}
                            disabled={settingsSaving}
                            aria-label={`Preview ${option.label} voice`}
                            aria-pressed={previewingVoice === option.id}
                            title={`Preview ${option.label}`}
                            data-preview-src={`/audio/voice-previews/${option.id}.mp3`}
                          >
                            <Volume2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </fieldset>

                  <fieldset className="vm-settings-fieldset">
                    <legend>Personality</legend>
                    <div className="vm-settings-options">
                      {personalityOptions.map((option) => (
                        <label key={option.id} className={draftPersonality === option.id ? 'vm-setting-option-selected' : ''}>
                          <input
                            type="radio"
                            name="voice-mode-personality"
                            value={option.id}
                            checked={draftPersonality === option.id}
                            onChange={() => setDraftPersonality(option.id)}
                          />
                          <span>
                            <strong>{option.label}</strong>
                            <em>{option.description}</em>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <fieldset className="vm-settings-fieldset">
                    <legend>Response length</legend>
                    <div className="vm-settings-options">
                      {responseLengthOptions.map((option) => (
                        <label key={option.id} className={draftResponseLength === option.id ? 'vm-setting-option-selected' : ''}>
                          <input
                            type="radio"
                            name="voice-mode-response-length"
                            value={option.id}
                            checked={draftResponseLength === option.id}
                            onChange={() => setDraftResponseLength(option.id)}
                          />
                          <span>
                            <strong>{option.label}</strong>
                            <em>{option.description}</em>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <fieldset className="vm-settings-fieldset">
                    <legend>Follow-up intensity</legend>
                    <div className="vm-settings-options">
                      {followUpIntensityOptions.map((option) => (
                        <label key={option.id} className={draftFollowUpIntensity === option.id ? 'vm-setting-option-selected' : ''}>
                          <input
                            type="radio"
                            name="voice-mode-follow-up-intensity"
                            value={option.id}
                            checked={draftFollowUpIntensity === option.id}
                            onChange={() => setDraftFollowUpIntensity(option.id)}
                          />
                          <span>
                            <strong>{option.label}</strong>
                            <em>{option.description}</em>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  {previewError ? <p className="vm-settings-error">{previewError}</p> : null}
                  {settingsError ? <p className="vm-settings-error">{settingsError}</p> : null}
                </Popup>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DoneFooter: React.FC<{
  answered: number;
  onStandardMode: () => void;
}> = ({ answered, onStandardMode }) => (
  <div className="vm-done-footer" data-testid="voice-completed-state">
    <p className="vm-eyebrow">Conversation complete</p>
    <h2>{answered > 0 ? `Saved. ${answered} ${answered === 1 ? 'answer' : 'answers'} captured.` : 'Saved.'}</h2>
    <p>{answered > 0
      ? 'Step into Standard mode to look back through what you said.'
      : 'Step into Standard mode to review this module.'}</p>
    <div>
      <button type="button" className="vm-primary-action" onClick={onStandardMode}>
        Complete module
      </button>
    </div>
  </div>
);

const StatusPip: React.FC<{ status: string }> = ({ status }) => (
  <span className={`vm-debug-status vm-debug-status-${status}`}>
    <span />
    {status}
  </span>
);

const DebugSection: React.FC<{
  title: string;
  badge?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({
  title,
  badge,
  open,
  onToggle,
  children,
}) => {
  return (
    <section className="vm-debug-section">
      <button type="button" onClick={onToggle} aria-expanded={open}>
        <span className={`vm-debug-chevron ${open ? 'vm-debug-chevron-open' : ''}`} aria-hidden="true" />
        <span>{title}</span>
        {badge ? <em>{badge}</em> : null}
      </button>
      {open ? <div className="vm-debug-section-body">{children}</div> : null}
    </section>
  );
};

const TokenDetailsBubble: React.FC<{
  modelLabel: string;
  tokenCounts?: VoiceFlowCostEstimate['realtime']['token_counts'];
}> = ({ modelLabel, tokenCounts }) => {
  const entries = getTokenCountEntries(tokenCounts);

  return (
    <span className="vm-debug-token-anchor" tabIndex={0}>
      {modelLabel}
      <span className="vm-debug-token-bubble" role="tooltip">
        <strong>{modelLabel} tokens</strong>
        {entries.length ? (
          <dl>
            {entries.map((entry) => (
              <React.Fragment key={entry.key}>
                <dt>{entry.label}</dt>
                <dd>{formatTokenCount(entry.value)}</dd>
              </React.Fragment>
            ))}
          </dl>
        ) : (
          <em>No token counts reported.</em>
        )}
      </span>
    </span>
  );
};

const DebugTranscriptRow: React.FC<{
  time: string;
  text: string;
  live?: boolean;
  assistant?: boolean;
}> = ({
  time,
  text,
  live = false,
  assistant = false,
}) => (
  <div className={`vm-debug-transcript-row ${live ? 'vm-debug-transcript-live' : ''}`}>
    <div>
      <span>{time}</span>
      {live ? <strong>Live</strong> : null}
      <em>{assistant ? 'assistant' : 'user'}</em>
    </div>
    <p>{text}</p>
  </div>
);

const VoiceDebugDrawer: React.FC<{
  open: boolean;
  onToggle: () => void;
  sessionId?: number | null;
  sessionStatus?: string | null;
  showClearCrisisAction?: boolean;
  userTranscriptEntries: TranscriptEntry[];
  assistantTranscriptEntries: AssistantEntry[];
  latestExtraction: Record<string, unknown> | null;
  questionAnswerRows: Array<{
    flowItemId: number;
    questionId: number;
    status: string;
    questionText: string;
    answerText: string;
  }>;
  recentRealtimeEvents: Array<{
    id: string;
    receivedAt: string;
    type: string;
    payload: Record<string, unknown>;
  }>;
  instructionBlock: string;
  costEstimate?: VoiceFlowCostEstimate | null;
  latencyMetrics?: VoiceFlowLatencyMetrics | null;
  aiSafety?: VoiceFlowAiSafetyState | null;
  runtime?: VoiceRuntime;
  defaultRuntime?: VoiceRuntime;
  debugRuntimeOverride?: VoiceRuntime | null;
  onSetRuntimeOverride?: (runtime: VoiceRuntime | null) => void;
  processingModels?: VoiceProcessingModels;
  defaultProcessingModels?: VoiceProcessingModels;
  previousProcessingModels?: VoiceProcessingModels | null;
  processingModelOptions?: VoiceProcessingModelOptions;
  processingModelsUnconfirmed?: boolean;
  processingModelsNotice?: string | null;
  onSetProcessingModelsOverride?: (models: Partial<VoiceProcessingModels> | null) => void;
  realtimeModel?: string | null;
  environmentRealtimeModel?: string | null;
  globalRealtimeModelOverride?: string | null;
  debugRealtimeModelOverride?: string | null;
  realtimeModelOptions?: Array<{
    id: string;
    label: string;
    description: string;
  }>;
  transcriptionModel?: string | null;
  environmentTranscriptionModel?: string | null;
  globalTranscriptionModelOverride?: string | null;
  debugTranscriptionModelOverride?: string | null;
  transcriptionModelOptions?: Array<{
    id: string;
    label: string;
    description: string;
  }>;
  debugActionBusy?: VoiceDebugActionBusy | null;
  debugActionError?: string | null;
  onClearCrisisPause: () => void;
  onResetRun: () => void;
  onSetRealtimeModelOverride: (realtimeModel: string | null) => void;
  onSetTranscriptionModelOverride: (transcriptionModel: string | null) => void;
}> = ({
  open,
  onToggle,
  sessionId,
  sessionStatus,
  showClearCrisisAction = false,
  userTranscriptEntries,
  assistantTranscriptEntries,
  latestExtraction,
  questionAnswerRows,
  recentRealtimeEvents,
  instructionBlock,
  costEstimate,
  latencyMetrics,
  aiSafety,
  runtime = 'realtime',
  defaultRuntime = 'realtime',
  debugRuntimeOverride,
  onSetRuntimeOverride,
  processingModels,
  defaultProcessingModels,
  previousProcessingModels,
  processingModelOptions,
  processingModelsUnconfirmed = false,
  processingModelsNotice,
  onSetProcessingModelsOverride,
  realtimeModel,
  environmentRealtimeModel,
  globalRealtimeModelOverride,
  debugRealtimeModelOverride,
  realtimeModelOptions = [],
  transcriptionModel,
  environmentTranscriptionModel,
  globalTranscriptionModelOverride,
  debugTranscriptionModelOverride,
  transcriptionModelOptions = [],
  debugActionBusy = null,
  debugActionError = null,
  onClearCrisisPause,
  onResetRun,
  onSetRealtimeModelOverride,
  onSetTranscriptionModelOverride,
}) => {
  const [drawerWidth, setDrawerWidth] = useState(() => loadDebugDrawerWidth());
  const [debugSectionState, setDebugSectionState] = useState<Record<VoiceDebugSectionId, boolean>>(
    () => loadDebugSectionState(),
  );

  const toggleDebugSection = useCallback((sectionId: VoiceDebugSectionId) => {
    setDebugSectionState((currentState) => {
      const nextState = {
        ...currentState,
        [sectionId]: !currentState[sectionId],
      };
      saveDebugSectionState(nextState);
      return nextState;
    });
  }, []);

  const getDebugSectionProps = useCallback((sectionId: VoiceDebugSectionId) => ({
    open: debugSectionState[sectionId],
    onToggle: () => toggleDebugSection(sectionId),
  }), [debugSectionState, toggleDebugSection]);

  const handleResizeStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const resizeDrawer = (moveEvent: MouseEvent) => {
      const nextWidth = clampDebugDrawerWidth(window.innerWidth - moveEvent.clientX);
      setDrawerWidth(nextWidth);
      saveDebugDrawerWidth(nextWidth);
    };
    const stopResize = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', resizeDrawer);
      window.removeEventListener('mouseup', stopResize);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', resizeDrawer);
    window.addEventListener('mouseup', stopResize);
  }, []);

  const handleResizeKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    setDrawerWidth((currentWidth) => {
      const nextWidth = clampDebugDrawerWidth(
        currentWidth + (event.key === 'ArrowLeft' ? 40 : -40),
      );
      saveDebugDrawerWidth(nextWidth);
      return nextWidth;
    });
  }, []);

  if (!open) {
    return (
      <aside className="vm-debug-rail" data-testid="voice-debug-drawer">
        <button type="button" aria-label="Open voice debug" onClick={onToggle}>
          <span aria-hidden="true" />
        </button>
        <p>Staff debug</p>
        <i aria-hidden="true" />
      </aside>
    );
  }

  return (
    <aside
      className="vm-debug-drawer"
      data-testid="voice-debug-drawer"
      style={{ width: `${drawerWidth}px` }}
    >
      <div
        className="vm-debug-resize-handle"
        role="separator"
        aria-label="Resize voice debug drawer"
        aria-orientation="vertical"
        aria-valuemin={DEBUG_DRAWER_MIN_WIDTH}
        aria-valuemax={getDebugDrawerMaxWidth()}
        aria-valuenow={Math.round(drawerWidth)}
        tabIndex={0}
        onMouseDown={handleResizeStart}
        onKeyDown={handleResizeKeyDown}
      />
      <div className="vm-debug-header">
        <div>
          <span aria-hidden="true" />
          <strong>Staff Debug</strong>
          <em>session {sessionId ?? 'pending'}{sessionStatus ? ` - ${sessionStatus}` : ''}</em>
        </div>
        <button type="button" aria-label="Close voice debug" onClick={onToggle}>
          <span aria-hidden="true" />
        </button>
      </div>

      <div className="vm-debug-scroll">
        <div className="vm-debug-metrics">
          <div>
            <p>Estimated Cost</p>
            <div>
              <strong>{formatCostUsd(costEstimate?.total_cost_usd_micros ?? 0)}</strong>
              <span>{costEstimate?.currency ?? 'USD'}</span>
            </div>
            <dl>
              <dt>
                <TokenDetailsBubble
                  modelLabel="Realtime"
                  tokenCounts={costEstimate?.realtime?.token_counts}
                />
              </dt>
              <dd>{formatCostUsd(costEstimate?.realtime?.cost_usd_micros ?? 0)}</dd>
              <dt>
                <TokenDetailsBubble
                  modelLabel="Transcription"
                  tokenCounts={costEstimate?.transcription?.token_counts}
                />
              </dt>
              <dd>{formatCostUsd(costEstimate?.transcription?.cost_usd_micros ?? 0)}</dd>
              {costEstimate?.live ? <>
                <dt>GPT Live 1</dt>
                <dd>{formatCostUsd(costEstimate.live.cost_usd_micros)}</dd>
                <dt>Live billed seconds</dt>
                <dd>{costEstimate.live.billable_seconds.toFixed(1)}</dd>
                <dt>Live connections</dt>
                <dd>{costEstimate.live.connection_count}</dd>
                <dt>Live usage</dt>
                <dd>{costEstimate.live.final_usage_confirmed ? 'Final reported' : 'Estimate; final usage pending'}</dd>
              </> : null}
            </dl>
          </div>
          <div role="group" aria-label="Last turn timing">
            <p>Last Turn Timing</p>
            <div>
              <span>Total backend time</span>
              <strong>{formatLatencyMs(latencyMetrics?.total_ms)}</strong>
            </div>
            <dl>
              <dt>safety status</dt>
              <dd>{aiSafety?.status ?? latencyMetrics?.ai_safety_status ?? '-'}</dd>
              <dt>safety time</dt>
              <dd>{formatLatencyMs(aiSafety?.latency_ms ?? latencyMetrics?.ai_safety_ms)}</dd>
              <dt>Evaluator total</dt>
              <dd>{formatLatencyMs(latencyMetrics?.extraction_ms)}</dd>
              {latencyMetrics?.extraction_phases ? <>
                <dt>Client setup</dt>
                <dd>{formatLatencyMs(latencyMetrics.extraction_phases.client_setup_ms)}</dd>
                <dt>Request preparation</dt>
                <dd>{formatLatencyMs(latencyMetrics.extraction_phases.request_build_ms)}</dd>
                <dt>Model request</dt>
                <dd>{formatLatencyMs(latencyMetrics.extraction_phases.provider_request_ms)}</dd>
                <dt>Response processing</dt>
                <dd>{formatLatencyMs(latencyMetrics.extraction_phases.response_processing_ms)}</dd>
                <dt>Query logging</dt>
                <dd>{formatLatencyMs(latencyMetrics.extraction_phases.query_logging_ms)}</dd>
                <dt>Request attempts</dt>
                <dd>{latencyMetrics.extraction_phases.attempts ?? 'n/a'}</dd>
                <dt>Evaluation outcome</dt>
                <dd>{latencyMetrics.extraction_phases.outcome?.replace(/_/g, ' ') ?? 'n/a'}</dd>
                {latencyMetrics.extraction_phases.requested_service_tier != null
                  || latencyMetrics.extraction_phases.service_tier != null ? <>
                  <dt>Requested speed</dt>
                  <dd>{formatEvaluationSpeed(latencyMetrics.extraction_phases.requested_service_tier)}</dd>
                  <dt>Actual speed</dt>
                  <dd>{formatEvaluationSpeed(latencyMetrics.extraction_phases.service_tier)}</dd>
                </> : null}
              </> : null}
              {latencyMetrics?.commit_ms != null ? <>
                <dt>Commit processing</dt>
                <dd>{formatLatencyMs(latencyMetrics.commit_ms)}</dd>
              </> : null}
            </dl>
          </div>
        </div>

        {onSetRuntimeOverride ? (
          <DebugSection title="Voice Mode version" badge="session test" {...getDebugSectionProps('runtime')}>
            <div className="vm-debug-model-panel">
            <p className="vm-debug-model-note">Switch this session without clearing saved answers. The current connection will restart.</p>
            {userTranscriptEntries.some((entry) => entry.isLive) ? (
              <p className="vm-debug-model-note">Unsaved speech will not transfer. Finish your answer before switching if you want to keep it.</p>
            ) : null}
            <dl>
              <dt>Session</dt><dd>{runtime === 'live' ? 'GPT Live 1' : 'Realtime'}</dd>
              <dt>Operations default</dt><dd>{defaultRuntime === 'live' ? 'GPT Live 1' : 'Realtime'}</dd>
            </dl>
            <div className="vm-debug-actions">
              {(['realtime', 'live'] as const).map((option) => (
                <button key={option} type="button"
                  className={`vm-debug-action-button ${runtime === option ? 'vm-debug-action-selected' : ''}`}
                  disabled={Boolean(debugActionBusy)} aria-pressed={runtime === option}
                  onClick={() => onSetRuntimeOverride(option)}>
                  {option === 'live' ? 'Use GPT Live 1' : 'Use Realtime 2.1'}
                </button>
              ))}
              <button type="button" className="vm-debug-action-button"
                disabled={Boolean(debugActionBusy) || !debugRuntimeOverride}
                onClick={() => onSetRuntimeOverride(null)}>
                Adopt Operations default
              </button>
            </div>
            </div>
          </DebugSection>
        ) : null}
        {processingModels && processingModelOptions && onSetProcessingModelsOverride ? (
          <DebugSection title="Processing models" badge="session test" {...getDebugSectionProps('processingModels')}>
            <div className="vm-debug-model-panel">
              <p className="vm-debug-model-note">Changes disconnect this session and preserve saved answers. Finish any unsaved speech first. Use Reconnect to continue when needed. Models stay pinned to this session.</p>
              <VoiceProcessingModelControls models={processingModels} options={processingModelOptions}
                labelPrefix="Session" disabled={Boolean(debugActionBusy) || processingModelsUnconfirmed} onChange={onSetProcessingModelsOverride} />
              {processingModelsNotice ? <p role="status" className="vm-debug-model-note">{processingModelsNotice}</p> : null}
              {defaultProcessingModels ? <dl>
                <dt>Operations evaluator</dt><dd>{defaultProcessingModels.evaluation.model} / {defaultProcessingModels.evaluation.reasoning_effort} · {formatEvaluationSpeed(defaultProcessingModels.evaluation.service_tier ?? 'default')}</dd>
                <dt>Operations safety</dt><dd>{defaultProcessingModels.safety.model} / {defaultProcessingModels.safety.reasoning_effort}</dd>
                <dt>Operations comparison</dt><dd>{defaultProcessingModels.safety_shadow
                  ? `${defaultProcessingModels.safety_shadow.model} / ${defaultProcessingModels.safety_shadow.reasoning_effort}` : 'Off'}</dd>
              </dl> : null}
              <div className="vm-debug-actions">
                <button type="button" className="vm-debug-action-button" disabled={Boolean(debugActionBusy) || processingModelsUnconfirmed}
                  onClick={() => onSetProcessingModelsOverride(null)}>Adopt Operations processing defaults</button>
                {previousProcessingModels ? <button type="button" className="vm-debug-action-button"
                  disabled={Boolean(debugActionBusy) || processingModelsUnconfirmed} onClick={() => onSetProcessingModelsOverride(previousProcessingModels)}>
                  Restore previous session models
                </button> : null}
              </div>
              <p className="vm-debug-model-note">Adopting defaults copies them once; later Operations changes do not affect this session.</p>
            </div>
          </DebugSection>
        ) : null}
        {runtime === 'realtime' ? <>
        <DebugSection title="Realtime Model" badge="session test" {...getDebugSectionProps('realtimeModel')}>
          <div className="vm-debug-model-panel">
            <dl>
              <dt>effective</dt>
              <dd>{realtimeModel ?? '-'}</dd>
              <dt>global default</dt>
              <dd>{globalRealtimeModelOverride ?? 'environment default'}</dd>
              <dt>environment</dt>
              <dd>{environmentRealtimeModel ?? '-'}</dd>
              <dt>session override</dt>
              <dd>{debugRealtimeModelOverride ?? 'none'}</dd>
            </dl>
            <div className="vm-debug-model-options" role="group" aria-label="Voice realtime model override">
              {realtimeModelOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`vm-debug-action-button ${realtimeModel === option.id ? 'vm-debug-action-selected' : ''}`}
                  onClick={() => onSetRealtimeModelOverride(option.id)}
                  disabled={Boolean(debugActionBusy)}
                  title={option.description}
                >
                  {debugActionBusy === 'realtime_model' && realtimeModel !== option.id ? 'Switching...' : option.label}
                </button>
              ))}
            </div>
            <p className="vm-debug-model-note">Temporary for this voice session only. Switching reconnects Realtime.</p>
          </div>
        </DebugSection>

        <DebugSection title="Transcription Model" badge="session test" {...getDebugSectionProps('transcriptionModel')}>
          <div className="vm-debug-model-panel">
            <dl>
              <dt>effective</dt>
              <dd>{transcriptionModel ?? '-'}</dd>
              <dt>global default</dt>
              <dd>{globalTranscriptionModelOverride ?? 'environment default'}</dd>
              <dt>environment</dt>
              <dd>{environmentTranscriptionModel ?? '-'}</dd>
              <dt>session override</dt>
              <dd>{debugTranscriptionModelOverride ?? 'none'}</dd>
            </dl>
            <div className="vm-debug-model-options vm-debug-model-options-three" role="group" aria-label="Voice transcription model override">
              {transcriptionModelOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`vm-debug-action-button ${transcriptionModel === option.id ? 'vm-debug-action-selected' : ''}`}
                  onClick={() => onSetTranscriptionModelOverride(option.id)}
                  disabled={Boolean(debugActionBusy)}
                  title={option.description}
                >
                  {debugActionBusy === 'transcription_model' && transcriptionModel !== option.id ? 'Switching...' : option.label}
                </button>
              ))}
            </div>
            <p className="vm-debug-model-note">Temporary for this voice session only. Switching reconnects Realtime.</p>
          </div>
        </DebugSection>

        </> : null}
        <DebugSection title="Staff Actions" badge="debug reset" {...getDebugSectionProps('staffActions')}>
          <div className="vm-debug-actions">
            {showClearCrisisAction ? (
              <button
                type="button"
                className="vm-debug-action-button"
                onClick={onClearCrisisPause}
                disabled={Boolean(debugActionBusy)}
              >
                {debugActionBusy === 'clear_crisis' ? 'Clearing...' : 'Clear crisis pause'}
              </button>
            ) : null}
            <button
              type="button"
              className="vm-debug-action-button vm-debug-action-danger"
              onClick={onResetRun}
              disabled={Boolean(debugActionBusy) || !sessionId}
            >
              {debugActionBusy === 'reset_run' ? 'Resetting...' : 'Reset current run'}
            </button>
            {debugActionError ? <p className="vm-debug-action-error">{debugActionError}</p> : null}
          </div>
        </DebugSection>

        <DebugSection title="Live Transcripts" badge="user - newest first" {...getDebugSectionProps('liveTranscripts')}>
          {userTranscriptEntries.length ? userTranscriptEntries.map((entry) => (
            <DebugTranscriptRow
              key={entry.id}
              time={formatShortTimestamp(entry.timestamp)}
              text={entry.text}
              live={entry.isLive}
            />
          )) : <p className="vm-debug-empty">No user transcripts yet.</p>}
        </DebugSection>

        <DebugSection title="Assistant Text (Debug)" badge="streamed" {...getDebugSectionProps('assistantText')}>
          {assistantTranscriptEntries.length ? assistantTranscriptEntries.map((entry) => (
            <DebugTranscriptRow
              key={entry.id}
              time={formatShortTimestamp(entry.timestamp)}
              text={entry.text}
              live={entry.isLive}
              assistant
            />
          )) : <p className="vm-debug-empty">No assistant text captured yet.</p>}
        </DebugSection>

        <DebugSection title="Extraction Result (Last Utterance)" badge="latest" {...getDebugSectionProps('extractionResult')}>
          <pre className="vm-debug-pre">{prettyJson(latestExtraction)}</pre>
        </DebugSection>

        <DebugSection title="Questions & Answers" badge={`${questionAnswerRows.length} items`} {...getDebugSectionProps('questionsAnswers')}>
          <div className="vm-debug-qa-table">
            <div className="vm-debug-qa-header">
              <span>Question ID</span>
              <span>Status</span>
              <span>Question Text</span>
              <span>Answer Text</span>
            </div>
            {questionAnswerRows.map((row) => (
              <div key={row.flowItemId} className="vm-debug-qa-row">
                <span>{row.questionId}</span>
                <span><StatusPip status={row.status} /></span>
                <span>{row.questionText}</span>
                <span>{row.answerText}</span>
              </div>
            ))}
          </div>
        </DebugSection>

        <DebugSection title="Realtime Events (Debug)" badge={`${recentRealtimeEvents.length} live`} {...getDebugSectionProps('realtimeEvents')}>
          <div className="vm-debug-events">
            {[...recentRealtimeEvents].reverse().map((event) => (
              <details key={event.id}>
                <summary>
                  <span>{formatShortTimestamp(event.receivedAt)}</span>
                  <strong>{event.type}</strong>
                  <em>{getRealtimeEventPreview(event.payload)}</em>
                </summary>
                <pre>{prettyJson(event.payload)}</pre>
              </details>
            ))}
            {!recentRealtimeEvents.length ? <p className="vm-debug-empty">No realtime events captured yet.</p> : null}
          </div>
        </DebugSection>

        <DebugSection title="Current Instructions (Debug)" badge="exact block sent" {...getDebugSectionProps('currentInstructions')}>
          <pre className="vm-debug-pre vm-debug-instructions">{instructionBlock || 'No instruction block.'}</pre>
        </DebugSection>

        {latencyMetrics?.recorded_at ? (
          <p className="vm-debug-recorded">Recorded {formatDebugTimestamp(latencyMetrics.recorded_at)}</p>
        ) : null}
      </div>
    </aside>
  );
};

export type VoiceFlowProps = QuestionFlowProps & { context?: VoiceSessionContext };

type VoiceFlowScreenProps = VoiceFlowProps & {
  initialVoiceBootstrap?: VoiceFlowSessionBootstrap | null;
  useSession?: (params: UseVoiceSessionParams) => UseVoiceSessionResult;
  onRuntimeChanged?: (bootstrap: VoiceFlowSessionBootstrap) => void;
  previousProcessingModels?: VoiceProcessingModels | null;
  onProcessingModelsChanged?: (bootstrap: VoiceFlowSessionBootstrap, notice?: string) => void;
  processingModelsNotice?: string | null;
};

// The parent keys this component by runtime and generation, keeping each
// mounted controller's hook and transport stable for its entire lifetime.
export const VoiceFlowScreen: React.FC<VoiceFlowScreenProps> = ({
  flowName,
  courseId = null,
  targetDate,
  flowRunId,
  initialBootstrap = null,
  initialVoiceBootstrap = null,
  initialFlow = null,
  context,
  useSession = useVoiceSession,
  onRuntimeChanged,
  previousProcessingModels,
  onProcessingModelsChanged,
  processingModelsNotice,
}) => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { track } = useEventTracking();
  const isStaff = Boolean(user?.is_staff);
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [debugActionBusy, setDebugActionBusy] = useState<VoiceDebugActionBusy | null>(null);
  const [debugActionError, setDebugActionError] = useState<string | null>(null);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [isThemeReady, setIsThemeReady] = useState(false);
  const [processingModelsUnconfirmed, setProcessingModelsUnconfirmed] = useState(false);
  const [debugConfirmAction, setDebugConfirmAction] = useState<VoiceDebugConfirmAction | null>(null);
  const [safetyNarrationLoading, setSafetyNarrationLoading] = useState(false);
  const [safetyNarrationPlaying, setSafetyNarrationPlaying] = useState(false);
  const debugConfirmPopupRef = useRef<PopupHandle | null>(null);
  const safetyNarrationAudioRef = useRef<HTMLAudioElement | null>(null);
  const safetyNarrationRequestIdRef = useRef(0);
  const safetyNarrationAutoPlayedKeyRef = useRef<string | null>(null);
  const learnerTranscriptHandlerRef = useRef<((text: string) => Promise<boolean>) | null>(null);
  const learnerTranscriptMetadataRef = useRef<((text: string) => Record<string, unknown>) | null>(null);
  const releaseRoleplayActorRef = useRef<(() => Promise<void>) | null>(null);
  const [roleplayQuestionId, setRoleplayQuestionId] = useState<number | null>(null);
  const [roleplayConversationArchive, setRoleplayConversationArchive] = useState<{
    scope: string; messages: RoleplayConversationMessage[];
  }>({ scope: '', messages: [] });
  const [roleplayHistoryRevision, setRoleplayHistoryRevision] = useState(0);
  const roleplayHistoryMountedRef = useRef(false);
  const roleplayHistoryRequestsRef = useRef({
    settled: new Set<string>(), active: new Map<number, string>(), nextId: 0,
  });
  const roleplayHistoryAuthorityRef = useRef({
    key: '', targets: new Map<string, RoleplayConversationHistoryTarget>(),
  });
  const roleplayLiveCaptionsRef = useRef<{
    scope: string; sessionId: number | null;
    learner: RoleplayLiveCaption | null; actor: RoleplayLiveCaption | null; nextId: number;
    actorGeneration: number | null;
  }>({ scope: '', sessionId: null, learner: null, actor: null, nextId: 0, actorGeneration: null });
  const completedSessionRef = useRef<number | null>(null);
  const completionRetryTimerRef = useRef<number | null>(null);
  const completionRetryCountRef = useRef(0);
  const finishModuleRef = useRef<() => Promise<void>>(async () => undefined);
  const completionNoticeRef = useRef<{ utterance: SpeechSynthesisUtterance; authorization: string; started: boolean } | null>(null);
  const flow = initialBootstrap?.flow ?? initialFlow;
  const resolvedFlowRunId = getResolvedFlowRunId(initialBootstrap, flowRunId);
  const {
    loading,
    error,
    session,
    connectionState,
    isReconnecting,
    micPermission,
    isMuted,
    idleWarningSeconds,
    isIdlePaused,
    stayConnected,
    assistantPhase,
    completionClosingPending,
    narrationConfirmationPending,
    confirmNarration,
    turnProcessing,
    turnHistory,
    assistantDebugHistory,
    liveUserDebugEntry,
    liveAssistantDebugEntry,
    recentRealtimeEvents,
    uiMode,
    resourcesPath,
    ctaLabel,
    currentTarget,
    progress,
    contentSchema,
    itemStates,
    instructionBlock,
    latestExtraction,
    connect,
    reconnect,
    refreshSession,
    clearCrisisPause,
    resetVoiceRun,
    setDebugRealtimeModelOverride,
    setDebugTranscriptionModelOverride,
    disconnect,
    interruptAssistant,
    toggleMute,
    replayAssistantMessage,
    executeCommand,
    flushPendingDraft,
    disconnectAsync,
    speak,
    speakConversational,
    terminalActionReady,
    automaticCompletionReady,
    canFinishAutomatically,
    capturePaused,
  } = useSession({
    flowName,
    flowRunId: resolvedFlowRunId,
    initialBootstrap: initialVoiceBootstrap,
    context,
    retainSafetyPolling: roleplayQuestionId !== null,
    onLearnerTranscript: async (text) => learnerTranscriptHandlerRef.current?.(text) ?? false,
    learnerTranscriptMetadata: (text) => learnerTranscriptMetadataRef.current?.(text) ?? {},
  });
  // A pull-to-refresh reload would cut off speech capture and playback in either runtime.
  useHoldPullToRefresh(connectionState === 'connecting' || connectionState === 'connected');
  const automaticFinishAuthorizationRef = useRef<string | null>(null);
  automaticFinishAuthorizationRef.current = automaticCompletionReady && terminalActionReady === 'finish'
    && session && !capturePaused && uiMode !== 'crisis'
    ? `${session.id}:${session.runtime_generation ?? 0}` : null;
  const automaticModuleClosing = session?.assistant_turn_directive?.auto_finish_after_response === true
    && (session.voice_runtime ?? 'realtime') === 'realtime' && session.voice_adapter_version === 2
    && session.context?.kind !== 'quiz_attempt' && context?.kind !== 'quiz_attempt';
  const cancelCompletionNotice = useCallback(() => {
    const notice = completionNoticeRef.current;
    if (!notice) return;
    completionNoticeRef.current = null;
    // Never cancel an unrelated announcement that was already using synthesis.
    // This fallback is started only on an idle synthesizer and owns its speech.
    notice.utterance.volume = 0;
    if (notice.started) window.speechSynthesis?.cancel();
  }, []);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const progressPercent = progress?.required_progress_percent ?? progress?.progress_percent ?? 0;
  const latencyMetrics = session?.debug?.latency_metrics ?? null;
  const realtimeModel = session?.realtime_model ?? session?.debug?.realtime_model ?? null;
  const environmentRealtimeModel = session?.environment_realtime_model ?? session?.debug?.environment_realtime_model ?? null;
  const globalRealtimeModelOverride = session?.global_realtime_model_override ?? session?.debug?.global_realtime_model_override ?? null;
  const debugRealtimeModelOverride = session?.debug_realtime_model_override ?? session?.debug?.debug_realtime_model_override ?? null;
  const realtimeModelOptions = session?.realtime_model_options ?? session?.debug?.realtime_model_options ?? [];
  const transcriptionModel = session?.transcription_model ?? session?.debug?.transcription_model ?? null;
  const environmentTranscriptionModel = session?.environment_transcription_model ?? session?.debug?.environment_transcription_model ?? null;
  const globalTranscriptionModelOverride = session?.global_transcription_model_override ?? session?.debug?.global_transcription_model_override ?? null;
  const debugTranscriptionModelOverride = session?.debug_transcription_model_override ?? session?.debug?.debug_transcription_model_override ?? null;
  const transcriptionModelOptions = session?.transcription_model_options ?? session?.debug?.transcription_model_options ?? [];
  const costEstimate = session?.cost_estimate ?? session?.debug?.cost_estimate ?? null;
  const aiSafety = session?.ai_safety ?? session?.debug?.ai_safety ?? null;
  const hasPendingCompletionClosingDirective = Boolean(
    session?.assistant_turn_directive?.should_create_response
    && session.assistant_turn_directive.response_purpose === 'completion_closing'
  );
  const itemStateMap = useMemo(
    () => new Map(itemStates.map((itemState) => [itemState.flow_item_id, itemState])),
    [itemStates],
  );
  const completedAnswerCount = contentSchema.filter((item) => (
    !item.is_passive
    && (itemStateMap.get(item.flow_item_id)?.status ?? item.status) === 'complete'
  )).length;
  const userTranscriptEntries = useMemo(() => (
    buildUserTranscriptEntries(turnHistory, liveUserDebugEntry)
      .sort((a, b) => parseTimestamp(b.timestamp, 0) - parseTimestamp(a.timestamp, 0))
  ), [liveUserDebugEntry, turnHistory]);
  const reconciledAssistantEntries = useMemo(() => (
    reconcileAssistantTranscriptEntries(turnHistory, assistantDebugHistory, liveAssistantDebugEntry)
  ), [assistantDebugHistory, liveAssistantDebugEntry, turnHistory]);
  const reconciledLiveAssistantEntry = reconciledAssistantEntries.find((entry) => (
    entry.id === liveAssistantDebugEntry?.id && entry.isLive
  )) ?? null;
  const assistantTranscriptEntries = useMemo(() => (
    [...reconciledAssistantEntries].reverse()
  ), [reconciledAssistantEntries]);
  const questionAnswerRows = useMemo(() => contentSchema.map((contentItem) => {
    const itemState = itemStateMap.get(contentItem.flow_item_id);
    return {
      flowItemId: contentItem.flow_item_id,
      questionId: contentItem.question_id,
      status: itemState?.status ?? contentItem.status ?? 'unanswered',
      questionText: contentItem.prompt,
      answerText: itemState?.answer_text?.trim() || contentItem.answer_text?.trim() || '-',
    };
  }), [contentSchema, itemStateMap]);

  const handleExitVoiceMode = useCallback(async () => {
    if (releaseRoleplayActorRef.current) await releaseRoleplayActorRef.current();
    if (flushPendingDraft && !await flushPendingDraft()) return;
    disconnect();
    const params = new URLSearchParams();
    params.set('mode', 'standard');
    const resolvedTargetDate = progress?.target_date ?? targetDate ?? null;
    if (resolvedTargetDate) {
      params.set('targetDate', resolvedTargetDate);
    }
    const exitFlowRunId =
      typeof progress?.flow_run_id === 'number'
        ? progress.flow_run_id
        : typeof resolvedFlowRunId === 'number'
          ? resolvedFlowRunId
          : null;
    if (typeof progress?.flow_run_id === 'number') {
      params.set('flowRunId', String(progress.flow_run_id));
    } else if (typeof resolvedFlowRunId === 'number') {
      params.set('flowRunId', String(resolvedFlowRunId));
    }

    if (typeof exitFlowRunId === 'number') {
      try {
        await flows.updateInteractionMode(flowName, {
          flowRunId: exitFlowRunId,
          mode: 'standard',
        });
      } catch (modeError) {
        console.warn('Unable to record standard mode for flow run', modeError);
      }
    }

    navigate({
      pathname: typeof courseId === 'number'
        ? `/courses/${courseId}/flows/${flowName}`
        : `/flows/${flowName}`,
      search: params.toString() ? `?${params.toString()}` : '',
    });
  }, [courseId, disconnect, flushPendingDraft, flowName, navigate, progress?.flow_run_id, progress?.target_date, resolvedFlowRunId, targetDate]);

  const handleSpokenLeave = useCallback(async () => {
    if (releaseRoleplayActorRef.current) await releaseRoleplayActorRef.current();
    if (flushPendingDraft && !await flushPendingDraft()) return;
    if (session) await voiceFlowApi.closeSession(flowName, session.id, false, session.runtime_generation ?? 0);
    disconnect();
    navigate(typeof courseId === 'number' ? '/training#courses' : '/home');
  }, [courseId, disconnect, flowName, flushPendingDraft, navigate, session]);

  useEffect(() => {
    if (roleplayQuestionId !== null || uiMode === 'crisis' || session?.status === 'crisis_paused') return;
    if (currentTarget?.question_type === 'roleplay' && typeof resolvedFlowRunId === 'number') {
      setRoleplayQuestionId(currentTarget.question_id);
    }
  }, [currentTarget, resolvedFlowRunId, roleplayQuestionId, session?.status, uiMode]);

  useEffect(() => {
    if (session?.command_action === 'defer' && roleplayQuestionId !== null
      && currentTarget?.question_id !== roleplayQuestionId) setRoleplayQuestionId(null);
  }, [currentTarget?.question_id, roleplayQuestionId, session?.command_action]);

  const integratedRoleplay = (session?.voice_runtime ?? 'realtime') === 'realtime'
    && session?.voice_adapter_version === 2 && typeof speakConversational === 'function';
  const roleplayConversationScope = `${user?.id ?? 'current'}:${flowName}:${context?.kind ?? session?.context?.kind ?? 'flow_run'}:${context?.id ?? session?.context?.id ?? resolvedFlowRunId ?? ''}`;
  const roleplayHistoryTargets = useMemo(() => typeof resolvedFlowRunId === 'number'
    ? getRoleplayConversationHistoryTargets({ contentSchema, itemStates, flowRunId: resolvedFlowRunId,
      responses: initialBootstrap?.responses ?? [],
      currentQuestionIds: [roleplayQuestionId, currentTarget?.question_type === 'roleplay' ? currentTarget.question_id : null] })
    : [], [contentSchema, currentTarget?.question_id, currentTarget?.question_type, initialBootstrap?.responses,
      itemStates, resolvedFlowRunId, roleplayQuestionId]);
  const roleplayHistoryAuthority = integratedRoleplay && session
    && typeof user?.id === 'number' && Number.isSafeInteger(user.id) && user.id > 0
    && typeof resolvedFlowRunId === 'number' && Number.isSafeInteger(resolvedFlowRunId) && resolvedFlowRunId > 0
    && context?.kind !== 'quiz_attempt' && session.context?.kind !== 'quiz_attempt'
    ? `${roleplayConversationScope}:${session.id}:${session.runtime_generation ?? 0}` : '';
  roleplayHistoryAuthorityRef.current = {
    key: roleplayHistoryAuthority,
    targets: new Map(roleplayHistoryTargets.map((target) => [`${roleplayConversationScope}:${target.questionId}`, target])),
  };
  useEffect(() => {
    roleplayHistoryMountedRef.current = true;
    return () => { roleplayHistoryMountedRef.current = false; };
  }, []);
  useEffect(() => {
    if (!roleplayHistoryAuthority || typeof resolvedFlowRunId !== 'number') return;
    const requests = roleplayHistoryRequestsRef.current;
    const requestedAuthority = roleplayHistoryAuthority;
    for (const [questionKey, target] of roleplayHistoryAuthorityRef.current.targets) {
      if (requests.active.size >= 3) break;
      if (requests.settled.has(questionKey) || [...requests.active.values()].includes(questionKey)) continue;
      const requestId = requests.nextId++;
      requests.active.set(requestId, questionKey);
      const currentTargetForRequest = () => roleplayHistoryMountedRef.current
        && roleplayHistoryAuthorityRef.current.key === requestedAuthority
        ? roleplayHistoryAuthorityRef.current.targets.get(questionKey) : undefined;
      void (async () => {
        try {
          // This read never mounts the actor controller, prepares an opener or
          // changes the selected question, progress, safety or microphone.
          const { data } = await roleplayApi.getSession(flowName, resolvedFlowRunId, target.questionId, { includeHistory: true });
          const currentHistoryTarget = currentTargetForRequest();
          if (!currentHistoryTarget) return;
          requests.settled.add(questionKey);
          const history = Array.isArray(data.session_history) ? data.session_history : data.session ? [data.session] : [];
          const messages = buildRoleplayConversationHistory(history, resolvedFlowRunId, currentHistoryTarget);
          if (messages.length) setRoleplayConversationArchive((previous) => currentTargetForRequest() ? ({
            scope: roleplayConversationScope,
            messages: mergeRoleplayConversationMessages(
              previous.scope === roleplayConversationScope ? previous.messages : [], messages,
            ),
          }) : previous);
        } catch {
          // History is optional presentation. An owned failure is bounded to
          // this mount and must never interrupt the current activity.
          if (currentTargetForRequest()) requests.settled.add(questionKey);
        } finally {
          requests.active.delete(requestId);
          // A superseded read is not marked settled: reconnect can re-arm it
          // after the old request releases its slot, without parallel copies.
          if (roleplayHistoryMountedRef.current) setRoleplayHistoryRevision((value) => value + 1);
        }
      })();
    }
  }, [flowName, resolvedFlowRunId, roleplayConversationScope, roleplayHistoryAuthority, roleplayHistoryRevision, roleplayHistoryTargets]);
  const retainRoleplayConversation = useCallback((roleplaySession: RoleplaySession) => {
    if (!integratedRoleplay || roleplaySession.flow_run_id !== resolvedFlowRunId) return;
    const messages = buildRoleplayConversationMessages({
      sessionId: roleplaySession.id,
      personaName: roleplaySession.scenario.persona.name,
      turns: roleplaySession.turns,
    });
    setRoleplayConversationArchive((previous) => ({
      scope: roleplayConversationScope,
      messages: mergeRoleplayConversationMessages(
        previous.scope === roleplayConversationScope ? previous.messages : [], messages,
      ),
    }));
  }, [integratedRoleplay, resolvedFlowRunId, roleplayConversationScope]);

  const roleplay = useVoiceRoleplayHost({
    flowName, flowRunId: resolvedFlowRunId,
    conversationScope: roleplayConversationScope,
    questionId: uiMode === 'crisis' || session?.status === 'crisis_paused' ? null : roleplayQuestionId,
    integrated: integratedRoleplay,
    voiceSettings: {
      personality: session?.voice_personality,
      responseLength: session?.voice_response_length,
      followUpIntensity: session?.voice_follow_up_intensity,
    },
    draft: (currentTarget?.structured_payload?.roleplay_host_draft ?? null) as VoiceRoleplayDraft | null,
    guide: {
      speak: (text) => speak?.(text),
      converse: integratedRoleplay ? speakConversational : undefined,
      release: async () => { if (disconnectAsync) await disconnectAsync(); else disconnect(); },
      reconnect,
      interrupt: integratedRoleplay ? interruptAssistant : undefined,
      command: integratedRoleplay && executeCommand ? async (command) => Boolean(await executeCommand(
        command === 'pause_capture' ? 'pause' : command === 'resume_capture' ? 'resume' : 'settings',
      )) : undefined,
    },
    onContinue: async (completedRoleplaySession) => {
      if (executeCommand) {
        const next = await executeCommand('continue_roleplay');
        if (!next) throw new Error('Unable to return to the module. Say continue to retry.');
      }
      retainRoleplayConversation(completedRoleplaySession);
      setRoleplayQuestionId(null);
      await refreshSession({ sendRealtimeUpdate: true });
      if (!executeCommand) await reconnect();
    },
    onLeave: async () => { setRoleplayQuestionId(null); await handleSpokenLeave(); },
    onCrisis: async (roleplaySession) => {
      if (executeCommand) await executeCommand('roleplay_crisis', { roleplay_session_id: roleplaySession.id });
      await refreshSession();
    },
  });
  learnerTranscriptHandlerRef.current = roleplay.handleGuideTranscript;
  learnerTranscriptMetadataRef.current = roleplay.transcriptMetadata;
  releaseRoleplayActorRef.current = roleplay.active ? roleplay.releaseActor : null;

  const currentRoleplaySession = integratedRoleplay
    && roleplay.controller.session?.flow_run_id === resolvedFlowRunId
    && roleplay.controller.session.scenario.question_id === roleplayQuestionId
    ? roleplay.controller.session : null;
  useEffect(() => {
    if (currentRoleplaySession) retainRoleplayConversation(currentRoleplaySession);
  }, [currentRoleplaySession, retainRoleplayConversation]);

  const roleplayControlMessages = useMemo<RoleplayConversationMessage[]>(() => integratedRoleplay
    ? (roleplay.controlTranscripts ?? []).filter((capture) => (
      capture.conversationScope === roleplayConversationScope && capture.flowRunId === resolvedFlowRunId
    )).map((capture) => ({
      id: capture.id, role: 'user', speaker: 'You', text: capture.text,
      timestamp: capture.timestamp, order: Date.parse(capture.timestamp),
    })) : [], [integratedRoleplay, resolvedFlowRunId, roleplay.controlTranscripts, roleplayConversationScope]);
  useEffect(() => {
    if (!roleplayControlMessages.length) return;
    // Keep controls visible after actor release and later module steps. They
    // remain mount-local presentation, separate from canonical actor history.
    setRoleplayConversationArchive((previous) => ({
      scope: roleplayConversationScope,
      messages: mergeRoleplayConversationMessages(
        previous.scope === roleplayConversationScope ? previous.messages : [], roleplayControlMessages,
      ),
    }));
  }, [roleplayControlMessages, roleplayConversationScope]);

  const currentRoleplayMessages = useMemo(() => {
    const live = roleplayLiveCaptionsRef.current;
    if (live.scope !== roleplayConversationScope || live.sessionId !== currentRoleplaySession?.id) {
      live.scope = roleplayConversationScope;
      live.sessionId = currentRoleplaySession?.id ?? null;
      live.learner = null;
      live.actor = null;
      live.actorGeneration = null;
    }
    if (!currentRoleplaySession) return [];
    if (live.actorGeneration !== (roleplay.liveActorGeneration ?? null)) {
      live.actor = null;
      live.actorGeneration = roleplay.liveActorGeneration ?? null;
    }
    (['learner', 'actor'] as const).forEach((speaker) => {
      const text = speaker === 'learner' ? roleplay.liveTranscript : roleplay.liveActorTranscript;
      if (speaker === 'learner' && roleplayControlMessages.at(-1)?.text.trim() === text?.trim()) {
        live.learner = null;
        return;
      }
      const previous = live[speaker];
      live[speaker] = text?.trim() ? {
        text,
        timestamp: previous?.timestamp ?? new Date().toISOString(),
        id: previous?.id ?? String(live.nextId++),
      } : null;
    });
    return buildRoleplayConversationMessages({
      sessionId: currentRoleplaySession.id,
      personaName: currentRoleplaySession.scenario.persona.name,
      turns: currentRoleplaySession.turns,
      learnerCaption: live.learner,
      actorCaption: live.actor,
      actorCaptionGeneration: roleplay.liveActorGeneration,
    });
  }, [currentRoleplaySession, roleplay.liveActorGeneration, roleplay.liveActorTranscript, roleplay.liveTranscript, roleplayConversationScope, roleplayControlMessages]);
  const conversationMessages = useMemo(() => {
    const guideMessages = buildConversationMessages({
      turnHistory, assistantDebugHistory: reconciledAssistantEntries, liveUserDebugEntry,
      liveAssistantDebugEntry: reconciledLiveAssistantEntry,
    });
    const roleplayMessages = integratedRoleplay ? mergeRoleplayConversationMessages(
      roleplayConversationArchive.scope === roleplayConversationScope ? roleplayConversationArchive.messages : [],
      [...currentRoleplayMessages, ...roleplayControlMessages],
    ) : [];
    return [...guideMessages, ...roleplayMessages].sort((left, right) => left.order - right.order);
  }, [currentRoleplayMessages, integratedRoleplay, reconciledAssistantEntries, reconciledLiveAssistantEntry,
    liveUserDebugEntry, roleplayConversationArchive, roleplayConversationScope, roleplayControlMessages, turnHistory]);
  const latestConversationMessage = conversationMessages[conversationMessages.length - 1];
  const latestConversationMessageId = latestConversationMessage?.id ?? '';
  const latestConversationMessageText = latestConversationMessage?.text ?? '';
  const latestGuideMessageId = useMemo(() => {
    const latestGuideMessage = [...conversationMessages]
      .reverse()
      .find((message) => message.role === 'guide' && message.text.trim());
    return latestGuideMessage?.id ?? null;
  }, [conversationMessages]);

  useEffect(() => {
    if (terminalActionReady === 'leave') void handleSpokenLeave();
  }, [handleSpokenLeave, terminalActionReady]);

  const continueCompletedCourse = useCallback(async (fromQuiz: boolean) => {
    if (typeof courseId !== 'number') {
      navigate('/home');
      return;
    }
    try {
      const { data: completion } = await courses.getCourseCompletion(courseId);
      if (completion?.is_completed) {
        navigate(`/course_complete?courseId=${courseId}`, { replace: true, state: {
          courseId, courseName: completion.course?.name, completedFlowName: flowName,
          completedAt: completion.completed_at ?? new Date().toISOString(),
          ...(fromQuiz ? { fromQuiz: true } : {}),
        } });
        return;
      }
      const { data: courseFlow } = await courses.getFlowByName(flowName, { courseId });
      const nextFlow = courseFlow?.next_flow;
      if (typeof nextFlow?.name === 'string' && nextFlow.name.trim()) {
        const nextPath = buildFlowPath({ flowName: nextFlow.name, courseId });
        const nextSupportsVoice = nextFlow.voice_mode_enabled === true && nextFlow.voice_mode_available === true;
        navigate(nextSupportsVoice ? `${nextPath}?mode=voice` : nextPath, { replace: true });
        return;
      }
      // This is also the standard completion destination when there is no next
      // module; its course progress can represent an unfinished earlier module.
      navigate(`/course_complete?courseId=${courseId}`, { replace: true, state: {
        courseId, courseName: courseFlow?.course?.name ?? completion?.course?.name,
        completedFlowName: flowName,
        ...(fromQuiz ? { fromQuiz: true } : {}),
      } });
    } catch {
      // The canonical module is already finished. A failed continuation lookup
      // should still land on a registered content route.
      navigate('/training#courses', { replace: true });
    }
  }, [courseId, flowName, navigate]);

  const handleFinishModule = useCallback(async () => {
    if (!session || completedSessionRef.current === session.id || completionRetryTimerRef.current !== null) return;
    const automaticAuthorization = automaticFinishAuthorizationRef.current;
    if (automaticAuthorization && completionRetryCountRef.current > 3) return;
    if (flushPendingDraft && !await flushPendingDraft()) {
      if (automaticAuthorization && automaticFinishAuthorizationRef.current === automaticAuthorization
        && completionRetryCountRef.current < 3) {
        const retry = completionRetryCountRef.current++;
        completionRetryTimerRef.current = window.setTimeout(() => {
          completionRetryTimerRef.current = null;
          if (automaticFinishAuthorizationRef.current === automaticAuthorization) void finishModuleRef.current();
        }, 1000 * (2 ** retry));
      }
      return;
    }
    if (automaticAuthorization && (automaticFinishAuthorizationRef.current !== automaticAuthorization
      || (canFinishAutomatically && !canFinishAutomatically()))) return;
    completedSessionRef.current = session.id;
    setCompletionError(null);
    try {
      const isQuizHost = session.context?.kind === 'quiz_attempt' || context?.kind === 'quiz_attempt';
      let quizSubmissionResult = session.quiz_submission_result;
      if (isQuizHost) {
        const attemptId = session.quiz_attempt_id ?? context?.id;
        if (typeof attemptId === 'number' && !quizSubmissionResult) {
          const submission = await quiz.submitCanonicalAttempt(attemptId);
          quizSubmissionResult = submission.data;
        }
      }
      await voiceFlowApi.closeSession(flowName, session.id, true, session.runtime_generation ?? 0);
      cancelCompletionNotice();
      disconnect();
      if (isQuizHost && (typeof courseId !== 'number' || quizSubmissionResult?.passed !== true)) {
        if (flow) navigate(`/quiz-results/${flow.id}${typeof courseId === 'number' ? `?courseId=${courseId}` : ''}`, {
          replace: true, state: {
            quizResults: quizSubmissionResult, flowName, courseId,
            // Results own their finale only after canonical Voice shutdown.
            flowTheme: flow.theme ?? null,
            themeTotal: contentSchema.length || progress?.total_item_count || progress?.total_questions || flow.questions?.length || 1,
            themeCompletionId: createThemeCompletionId(),
            themeModule: {
              key: flow.key, name: flow.name, title: flow.title,
              estimated_duration_minutes: flow.estimated_duration_minutes,
              theme_content: flow.theme_content,
            },
          },
        });
        else navigate('/training#courses', { replace: true });
        return;
      }
      await continueCompletedCourse(isQuizHost);
    } catch {
      completedSessionRef.current = null;
      if (automaticAuthorization && automaticFinishAuthorizationRef.current !== automaticAuthorization) return;
      if (automaticAuthorization && automaticFinishAuthorizationRef.current === automaticAuthorization) {
        const retries = completionRetryCountRef.current++;
        const message = retries < 3
          ? 'I couldn’t finish the module just yet. I’m trying again.'
          : 'I couldn’t finish the module. Your answers are safe. You can reconnect to try again.';
        setCompletionError(message);
        const synthesis = window.speechSynthesis;
        if (synthesis && typeof SpeechSynthesisUtterance !== 'undefined' && !synthesis.speaking && !synthesis.pending
          && !completionNoticeRef.current) {
          const utterance = new SpeechSynthesisUtterance(message);
          const notice = { utterance, authorization: automaticAuthorization, started: false };
          completionNoticeRef.current = notice;
          utterance.onstart = () => {
            notice.started = true;
            if (completionNoticeRef.current !== notice || automaticFinishAuthorizationRef.current !== notice.authorization) {
              utterance.volume = 0;
              synthesis.cancel();
            }
          };
          const release = () => { if (completionNoticeRef.current === notice) completionNoticeRef.current = null; };
          utterance.onend = release;
          utterance.onerror = release;
          synthesis.speak(utterance);
        }
        if (retries < 3) completionRetryTimerRef.current = window.setTimeout(() => {
          completionRetryTimerRef.current = null;
          if (automaticFinishAuthorizationRef.current === automaticAuthorization) void finishModuleRef.current();
        }, 1000 * (2 ** retries));
      } else setDebugActionError('Unable to finish the module. Your saved answers are still available. Say finish again to retry.');
    }
  }, [cancelCompletionNotice, canFinishAutomatically, contentSchema.length, context, continueCompletedCourse, courseId, disconnect, flow, flowName, flushPendingDraft, navigate, progress?.total_item_count, progress?.total_questions, session]);
  finishModuleRef.current = handleFinishModule;

  useEffect(() => {
    if (!automaticCompletionReady || (completionNoticeRef.current
      && completionNoticeRef.current.authorization !== automaticFinishAuthorizationRef.current)) {
      if (completionRetryTimerRef.current !== null) window.clearTimeout(completionRetryTimerRef.current);
      completionRetryTimerRef.current = null;
      completionRetryCountRef.current = 0;
      setCompletionError(null);
      cancelCompletionNotice();
    }
  }, [automaticCompletionReady, cancelCompletionNotice, capturePaused, session?.id, session?.runtime_generation, uiMode]);

  useEffect(() => () => {
    if (completionRetryTimerRef.current !== null) window.clearTimeout(completionRetryTimerRef.current);
    cancelCompletionNotice();
  }, [cancelCompletionNotice]);

  useEffect(() => {
    if (terminalActionReady === 'finish') void handleFinishModule();
  }, [handleFinishModule, terminalActionReady]);

  const handleClearCrisisPause = useCallback(async () => {
    setDebugActionBusy('clear_crisis');
    setDebugActionError(null);
    try {
      const nextSession = await clearCrisisPause();
      if (!nextSession) {
        setDebugActionError('Unable to clear crisis pause.');
        return;
      }
      debugConfirmPopupRef.current?.close();
    } finally {
      setDebugActionBusy(null);
    }
  }, [clearCrisisPause]);

  const handleResetVoiceRun = useCallback(async () => {
    setDebugActionBusy('reset_run');
    setDebugActionError(null);
    try {
      const nextSession = await resetVoiceRun();
      if (!nextSession) {
        setDebugActionError('Unable to reset current run.');
        return;
      }

      const params = new URLSearchParams();
      const resolvedTargetDate = nextSession.progress?.target_date ?? targetDate ?? null;
      if (resolvedTargetDate) {
        params.set('targetDate', resolvedTargetDate);
      }
      params.set('flowRunId', String(nextSession.flow_run_id));
      navigate({
        pathname: typeof courseId === 'number'
          ? `/courses/${courseId}/flows/${flowName}`
          : `/flows/${flowName}`,
        search: `?${params.toString()}`,
      }, { replace: true });
      debugConfirmPopupRef.current?.close();
    } finally {
      setDebugActionBusy(null);
    }
  }, [courseId, flowName, navigate, resetVoiceRun, targetDate]);

  const handleConfirmDebugAction = useCallback(() => {
    if (debugConfirmAction === 'clear_crisis') {
      void handleClearCrisisPause();
      return;
    }
    if (debugConfirmAction === 'reset_run') {
      void handleResetVoiceRun();
    }
  }, [debugConfirmAction, handleClearCrisisPause, handleResetVoiceRun]);

  const handleSetRealtimeModelOverride = useCallback(async (nextModel: string | null) => {
    setDebugActionBusy('realtime_model');
    setDebugActionError(null);
    try {
      const nextSession = await setDebugRealtimeModelOverride(nextModel);
      if (!nextSession) {
        setDebugActionError('Unable to update realtime model.');
      }
    } finally {
      setDebugActionBusy(null);
    }
  }, [setDebugRealtimeModelOverride]);

  const handleSetRuntimeOverride = useCallback(async (runtime: VoiceRuntime | null) => {
    if (!session || !onRuntimeChanged) return;
    if (releaseRoleplayActorRef.current) await releaseRoleplayActorRef.current();
    if (flushPendingDraft && !await flushPendingDraft()) return;
    setDebugActionBusy('runtime');
    setDebugActionError(null);
    disconnect();
    try {
      const { data } = await voiceFlowApi.setRuntimeOverride(flowName, session.id, runtime);
      onRuntimeChanged(data);
    } catch {
      setDebugActionError('Unable to switch Voice Mode version. Reconnect to continue with the saved version.');
    } finally {
      setDebugActionBusy(null);
    }
  }, [disconnect, flowName, flushPendingDraft, onRuntimeChanged, session]);

  const handleSetProcessingModelsOverride = useCallback(async (models: Partial<VoiceProcessingModels> | null) => {
    if (!session || !onProcessingModelsChanged || debugActionBusy || processingModelsUnconfirmed) return;
    if (releaseRoleplayActorRef.current) await releaseRoleplayActorRef.current();
    if (flushPendingDraft && !await flushPendingDraft()) return;
    setDebugActionBusy('processing_models');
    setDebugActionError(null);
    disconnect();
    try {
      const { data } = await voiceFlowApi.setProcessingModelsOverride(
        flowName, session.id, models, session.runtime_generation ?? 0,
      );
      onProcessingModelsChanged(data);
    } catch {
      try {
        const { data } = await voiceFlowApi.getProcessingModels(flowName, session.id);
        onProcessingModelsChanged({ ...data, client_secret: null, can_connect: false },
          'Could not confirm the update. Reloaded the saved session models. Review them, then reconnect when ready.');
      } catch {
        setProcessingModelsUnconfirmed(true);
        setDebugActionError('Could not confirm the saved processing models. Reload this page before changing models again.');
      }
    } finally {
      setDebugActionBusy(null);
    }
  }, [debugActionBusy, disconnect, flowName, flushPendingDraft, onProcessingModelsChanged, processingModelsUnconfirmed, session]);

  const handleSetTranscriptionModelOverride = useCallback(async (nextModel: string | null) => {
    setDebugActionBusy('transcription_model');
    setDebugActionError(null);
    try {
      const nextSession = await setDebugTranscriptionModelOverride(nextModel);
      if (!nextSession) {
        setDebugActionError('Unable to update transcription model.');
      }
    } finally {
      setDebugActionBusy(null);
    }
  }, [setDebugTranscriptionModelOverride]);

  const handleCloseDebugConfirm = useCallback(() => {
    setDebugConfirmAction(null);
    setDebugActionError(null);
  }, []);

  const awaitingManualReconnect = connectionState === 'idle' && Boolean(session)
    && ((session?.voice_runtime ?? 'realtime') === 'realtime'
      ? !session?.client_secret : session?.can_connect === false);
  const actorOwnsMicrophone = roleplay.active && roleplay.phase === 'practice';
  const guideRuntimeStatus = getRuntimeStatus({
    sessionStatus: uiMode === 'crisis' ? 'crisis_paused' : session?.status,
    hasCurrentTarget: Boolean(currentTarget),
    connectionState: awaitingManualReconnect ? 'disconnected' : connectionState,
    micPermission,
    isMuted,
    assistantPhase,
    completionClosingPending: completionClosingPending || hasPendingCompletionClosingDirective,
    turnProcessing,
    isIdlePaused: session?.voice_runtime === 'live' && Boolean(isIdlePaused),
  });
  const runtimeStatus: VoiceRuntimeStatus = actorOwnsMicrophone
    ? roleplay.actorActivity === 'speaking' || roleplay.actorActivity === 'responding' ? 'speaking'
      : roleplay.actorActivity === 'thinking' || roleplay.actorActivity === 'finalizing' ? 'thinking'
        : roleplay.actorState === 'connecting' ? 'connecting' : 'listening'
    : isReconnecting && uiMode !== 'crisis' ? 'reconnecting' : guideRuntimeStatus;
  // The server's selected items and current target drive the same module scene
  // as Standard. Captions, delivery receipts and local speech never drive it.
  const themeTotal = contentSchema.length || progress?.total_item_count || progress?.total_questions || 0;
  const themeTargetIndex = currentTarget
    ? contentSchema.findIndex((item) => item.flow_item_id === currentTarget.flow_item_id) : -1;
  const themeCompleted = Math.min(themeTotal, Math.max(0,
    progress?.completed_item_count ?? itemStates.filter((item) => item.status === 'complete').length));
  const themePosition = Math.min(Math.max(0, themeTargetIndex >= 0
    ? themeTargetIndex : progress?.current_index ?? themeCompleted), Math.max(0, themeTotal - 1));
  const themeProgress: FlowThemeProgress | null = flow && themeTotal > 0
    ? { step: themePosition, total: themeTotal, complete: false } : null;
  const themeQuestion = useMemo<Question | null>(() => currentTarget ? ({
    id: currentTarget.question_id, text: currentTarget.prompt, header: currentTarget.header,
    question_type: currentTarget.question_type, category: '', is_active: true, order: themePosition,
  }) : null, [currentTarget, themePosition]);
  const themeResponses = useMemo<Record<number, ResponsePayload>>(() => Object.fromEntries(
    itemStates.filter((item) => item.status === 'complete').map((item) => [item.question_id, {
      question: item.question_id, is_completed: true,
    }]),
  ), [itemStates]);
  const themeRunKey = `${flowName}:voice:${context?.kind ?? session?.context?.kind ?? 'flow_run'}:${context?.id ?? session?.context?.id ?? resolvedFlowRunId ?? 'pending'}`;
  const themeNavigation = useRef<{ key: string; position: number; direction: 1 | -1 }>({ key: themeRunKey, position: themePosition, direction: 1 });
  if (themeNavigation.current.key !== themeRunKey) {
    themeNavigation.current = { key: themeRunKey, position: themePosition, direction: 1 };
  } else if (themeNavigation.current.position !== themePosition) {
    themeNavigation.current = { key: themeRunKey, position: themePosition,
      direction: themePosition < themeNavigation.current.position ? -1 : 1 };
  }
  const dynamicThemeContext = useLearnerThemeContext({
    flow, question: themeQuestion, firstName: user?.first_name,
    position: themePosition, total: themeTotal, completed: themeCompleted,
    // Run progress may reach 100% before closing audio drains. The Voice host
    // leaves finale authority with its existing owned finish/shutdown path.
    complete: false, status: progress?.status ?? 'in_progress', responses: themeResponses,
    loading, saving: turnProcessing, error: Boolean(error), direction: themeNavigation.current.direction,
    runKey: themeRunKey,
  });
  const backToThemeCourse = useCallback(() => {
    if (courseId !== null) navigate(`/training?courseId=${courseId}#courses`);
  }, [courseId, navigate]);
  const themeLayer = <FlowThemeLayer theme={flow?.theme} progress={themeProgress}
    themeContext={dynamicThemeContext} hidden={actorOwnsMicrophone}
    onBackToCourse={courseId !== null ? backToThemeCourse : undefined} onReadyChange={setIsThemeReady} />;
  const layoutThemeProgress = actorOwnsMicrophone ? null : themeProgress;

  const showPermission = runtimeStatus === 'permission';
  const showIdlePaused = runtimeStatus === 'idle_paused' && uiMode !== 'crisis';
  const showIdleWarning = session?.voice_runtime === 'live' && idleWarningSeconds != null
    && !isIdlePaused && connectionState === 'connected'
    && session?.status !== 'crisis_paused' && uiMode !== 'crisis';
  const showError = !actorOwnsMicrophone && !showIdlePaused && (runtimeStatus === 'error' || runtimeStatus === 'disconnected'
    || runtimeStatus === 'reconnecting' || Boolean(error));
  const showThinking = turnProcessing && !showPermission;
  const showDone = runtimeStatus === 'done';
  const showCrisisNotice = session?.status === 'crisis_paused' || uiMode === 'crisis';
  const canNarrateSafetyNotice = session?.status === 'crisis_paused' && typeof session?.id === 'number';
  const safetyNarrationKey = canNarrateSafetyNotice && session?.id
    ? `${session.id}:${resourcesPath || ''}:${ctaLabel || ''}`
    : null;
  const handleSafetyCtaClick = useCallback(() => {
    if (!resourcesPath || !ctaLabel) {
      return;
    }
    track('button_click', {
      action: 'support_cta_click',
      control_id: 'voice_safety_notice_resources',
      support_target: uiMode === 'crisis' || session?.status === 'crisis_paused' ? 'emergency_resources' : 'support_resources',
      support_surface: 'voice_safety_notice',
      destination: resourcesPath,
      cta_label: ctaLabel,
    });
  }, [ctaLabel, resourcesPath, session?.status, track, uiMode]);

  const stopSafetyNarration = useCallback(() => {
    safetyNarrationRequestIdRef.current += 1;
    const audio = safetyNarrationAudioRef.current;
    safetyNarrationAudioRef.current = null;
    if (audio) {
      audio.pause?.();
      audio.src = '';
    }
    setSafetyNarrationLoading(false);
    setSafetyNarrationPlaying(false);
  }, []);

  const playSafetyNarration = useCallback(async () => {
    if (!canNarrateSafetyNotice || !session?.id) {
      return;
    }

    stopSafetyNarration();
    const requestId = safetyNarrationRequestIdRef.current + 1;
    safetyNarrationRequestIdRef.current = requestId;
    setSafetyNarrationLoading(true);
    setSafetyNarrationPlaying(false);

    try {
      const narration = await voiceFlowApi.getSafetyNarration(flowName, session.id);
      if (safetyNarrationRequestIdRef.current !== requestId) {
        return;
      }

      const audio = new Audio(narration.audio_url);
      safetyNarrationAudioRef.current = audio;
      audio.onended = () => {
        if (safetyNarrationAudioRef.current === audio) {
          safetyNarrationAudioRef.current = null;
        }
        setSafetyNarrationPlaying(false);
        setSafetyNarrationLoading(false);
      };
      audio.onerror = () => {
        if (safetyNarrationAudioRef.current === audio) {
          safetyNarrationAudioRef.current = null;
        }
        setSafetyNarrationPlaying(false);
        setSafetyNarrationLoading(false);
      };

      await audio.play();
      if (safetyNarrationRequestIdRef.current !== requestId) {
        audio.pause();
        return;
      }
      setSafetyNarrationPlaying(true);
    } catch {
      if (safetyNarrationRequestIdRef.current === requestId) {
        setSafetyNarrationPlaying(false);
      }
    } finally {
      if (safetyNarrationRequestIdRef.current === requestId) {
        setSafetyNarrationLoading(false);
      }
    }
  }, [canNarrateSafetyNotice, flowName, session?.id, stopSafetyNarration]);

  const handleSaveVoiceSettings = useCallback(async ({
    voice,
    personality,
    responseLength,
    followUpIntensity,
  }: {
    voice: string;
    personality: string;
    responseLength: string;
    followUpIntensity: string;
  }) => {
    const previousVoice = session?.voice ?? 'cedar';
    const previousPersonality = session?.voice_personality ?? 'warm_practical';
    const previousResponseLength = session?.voice_response_length ?? 'balanced';
    const previousFollowUpIntensity = session?.voice_follow_up_intensity ?? 'standard';
    const voiceChanged = voice !== previousVoice;
    const personalityChanged = personality !== previousPersonality;
    const responseLengthChanged = responseLength !== previousResponseLength;
    const followUpIntensityChanged = followUpIntensity !== previousFollowUpIntensity;
    const instructionSettingsChanged = personalityChanged || responseLengthChanged || followUpIntensityChanged;
    if (!voiceChanged && !instructionSettingsChanged) {
      return;
    }

    setSettingsSaving(true);
    setSettingsError(null);
    try {
      const updatedProfile = await userApi.updateProfile({
        settings: {
          voice_mode_voice: voice,
          voice_mode_personality: personality,
          voice_mode_response_length: responseLength,
          voice_mode_follow_up_intensity: followUpIntensity,
        },
      });
      updateUser({
        settings: {
          question_flow_tts_enabled: Boolean(updatedProfile?.settings?.question_flow_tts_enabled),
          question_flow_tts_available: Boolean(updatedProfile?.settings?.question_flow_tts_available),
          voice_mode_voice: updatedProfile?.settings?.voice_mode_voice ?? voice,
          voice_mode_personality: updatedProfile?.settings?.voice_mode_personality ?? personality,
          voice_mode_response_length: updatedProfile?.settings?.voice_mode_response_length ?? responseLength,
          voice_mode_follow_up_intensity: updatedProfile?.settings?.voice_mode_follow_up_intensity ?? followUpIntensity,
        },
      });

      const hasActiveRealtimeConnection = connectionState === 'connected' || connectionState === 'connecting';
      if (voiceChanged && hasActiveRealtimeConnection) {
        await reconnect();
        return;
      }

      await refreshSession({
        sendRealtimeUpdate: instructionSettingsChanged && connectionState === 'connected',
      });
    } catch (saveError) {
      const fallback = 'Unable to save voice settings.';
      const responseMessage = (
        saveError as { response?: { data?: Record<string, string[] | string> } }
      )?.response?.data;
      const firstError = responseMessage && Object.values(responseMessage)[0];
      setSettingsError(Array.isArray(firstError) ? firstError[0] : String(firstError || fallback));
      throw saveError;
    } finally {
      setSettingsSaving(false);
    }
  }, [
    connectionState,
    reconnect,
    refreshSession,
    session?.voice,
    session?.voice_personality,
    session?.voice_response_length,
    session?.voice_follow_up_intensity,
    updateUser,
  ]);

  useEffect(() => {
    const scrollNode = scrollRef.current;
    if (!scrollNode) {
      return;
    }
    const animationFrame = window.requestAnimationFrame(() => {
      scrollNode.scrollTop = scrollNode.scrollHeight;
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [conversationMessages.length, latestConversationMessageId, latestConversationMessageText, showThinking, showDone, narrationConfirmationPending]);

  useEffect(() => {
    if (!safetyNarrationKey) {
      safetyNarrationAutoPlayedKeyRef.current = null;
      stopSafetyNarration();
      return;
    }
    if (safetyNarrationAutoPlayedKeyRef.current === safetyNarrationKey) {
      return;
    }
    safetyNarrationAutoPlayedKeyRef.current = safetyNarrationKey;
    void playSafetyNarration();
  }, [playSafetyNarration, safetyNarrationKey, stopSafetyNarration]);

  useEffect(() => () => {
    stopSafetyNarration();
  }, [stopSafetyNarration]);

  useFlowThemeTransitionReady(!loading && Boolean(flow), { unthemed: !layoutThemeProgress });

  if (loading || !flow) {
    return <ThemeContextProvider value={dynamicThemeContext}>
      {themeLayer}
      <QuestionFlowLoadingState frosted={Boolean(layoutThemeProgress)} />
    </ThemeContextProvider>;
  }

  return (
    <ThemeContextProvider value={dynamicThemeContext}>
    {themeLayer}
    <QuestionLayout progress={progressPercent} flowName={flow.title || flow.name} flowIdentifier={flow.name} courseId={courseId}
      theme={flow.theme} themeProgress={layoutThemeProgress} themeReady={isThemeReady}>
      <div className="vm-root" data-voice-themed={layoutThemeProgress ? 'true' : undefined}
        onPointerDownCapture={() => stayConnected?.()}
        onKeyDownCapture={() => stayConnected?.()}
        onWheelCapture={() => stayConnected?.()}
      >
        <VoiceControls
          status={runtimeStatus}
          onStandardMode={() => { void handleExitVoiceMode(); }}
          voice={session?.voice ?? 'cedar'}
          personality={session?.voice_personality ?? 'warm_practical'}
          responseLength={session?.voice_response_length ?? 'balanced'}
          followUpIntensity={session?.voice_follow_up_intensity ?? 'standard'}
          voiceOptions={session?.voice_options ?? []}
          personalityOptions={session?.voice_personality_options ?? []}
          responseLengthOptions={session?.voice_response_length_options ?? []}
          followUpIntensityOptions={session?.voice_follow_up_intensity_options ?? []}
          settingsSaving={settingsSaving}
          settingsError={settingsError}
          onSaveSettings={handleSaveVoiceSettings}
        />

        <main className="vm-main" ref={scrollRef} data-testid="voice-conversation-thread">
          <div className="vm-thread">
            {showPermission ? <PermissionNotice onEnable={() => { void connect(); }} /> : null}

            {currentTarget?.original_question && currentTarget.evaluation?.original_answer ? (
              <details key={currentTarget.flow_item_id} className="vm-question-context" data-testid="voice-question-context">
                <summary>Question and earlier answers</summary>
                <div className="vm-question-context-body">
                  <AnswerReferenceContext.Provider value={{ mode: 'runtime', references: currentTarget.original_question_references ?? {} }}>
                    <QuestionText text={currentTarget.original_question} className="text-base" answerReferences />
                  </AnswerReferenceContext.Provider>
                  <p className="vm-question-context-answer">
                    {currentTarget.evaluation.original_answer.text_value
                      ?? currentTarget.evaluation.original_answer.text_list?.join('\n')}
                  </p>
                  {currentTarget.evaluation.turns.filter((turn) => turn.answer !== null).map((turn, index) => (
                    <React.Fragment key={index}>
                      <p>{turn.question}</p>
                      <p className="vm-question-context-answer">{turn.answer}</p>
                    </React.Fragment>
                  ))}
                </div>
              </details>
            ) : null}

            {!showPermission ? conversationMessages.map((message) => (
              message.role === 'guide' || message.role === 'actor' ? (
                <GuideMessage
                  key={message.id}
                  speaker={message.speaker}
                  streaming={message.isStreaming}
                  onReplay={message.id === latestGuideMessageId
                    ? () => replayAssistantMessage(message.text)
                    : undefined}
                  replayDisabled={
                    Boolean(message.isStreaming)
                    || actorOwnsMicrophone
                    || connectionState !== 'connected'
                    || assistantPhase === 'responding'
                    || turnProcessing
                  }
                >
                  {message.text}
                </GuideMessage>
              ) : (
                <UserMessage key={message.id} speaker={message.speaker} live={message.isLive}>
                  {message.text}
                </UserMessage>
              )
            )) : null}

            {showCrisisNotice ? (
              <SafetyNotice
                mode="crisis"
                resourcesPath={resourcesPath}
                ctaLabel={ctaLabel}
                onCtaClick={handleSafetyCtaClick}
                onNarrationReplay={canNarrateSafetyNotice ? () => { void playSafetyNarration(); } : undefined}
                narrationLoading={safetyNarrationLoading}
                narrationPlaying={safetyNarrationPlaying}
              />
            ) : null}
            {uiMode === 'supportive_distress' ? (
              <SafetyNotice
                mode="supportive_distress"
                resourcesPath={resourcesPath}
                ctaLabel={ctaLabel}
                onCtaClick={handleSafetyCtaClick}
              />
            ) : null}

            {showThinking ? <ThinkingRow /> : null}
            {capturePaused && !actorOwnsMicrophone ? <p role="status">Capture is paused. Say “resume capture” to continue. Your microphone remains available for commands.</p> : null}
            {session?.adapter_compatibility_reason ? <p role="alert">{session.adapter_compatibility_reason}</p> : null}
            {roleplay.error ? <p role="alert">{roleplay.error}</p> : null}

            {completionClosingPending && narrationConfirmationPending && confirmNarration && !automaticModuleClosing && !showCrisisNotice && !showIdlePaused ? (
              <div className="vm-done-footer" data-testid="voice-narration-confirmation">
                <p>{executeCommand ? 'Say “finish conversation” when you have finished listening.' : 'When you have finished listening, finish this conversation.'}</p>
                <button type="button" className="vm-primary-action" disabled={turnProcessing}
                  onClick={() => { if (executeCommand) void executeCommand('finish'); else void confirmNarration(); }}>
                  Finish conversation
                </button>
              </div>
            ) : null}

            {completionError ? <p role="status">{completionError}</p> : null}

            {showError && !showPermission ? (
              <ErrorNotice error={isReconnecting ? 'Reconnecting to voice mode. Your saved progress is safe.' : error}
                reconnecting={Boolean(isReconnecting)} onReconnect={reconnect} />
            ) : null}

            {showDone ? (
              <DoneFooter
                answered={completedAnswerCount}
                onStandardMode={() => { void handleExitVoiceMode(); }}
              />
            ) : null}
          </div>
        </main>

        {!showPermission && !actorOwnsMicrophone ? (
          <div className="vm-bottom-controls">
            {showIdleWarning ? (
              <div className="vm-idle-notice" data-testid="voice-idle-warning">
                <p role="status">Still there? Voice Mode will pause soon unless you speak or choose Stay connected.</p>
                <p aria-hidden="true">{idleWarningSeconds === 0
                  ? 'Pausing conversation…'
                  : `Pausing in ${idleWarningSeconds} seconds.`}</p>
                <button type="button" className="vm-secondary-action" onClick={() => stayConnected?.()}>
                  Stay connected
                </button>
              </div>
            ) : null}
            {showIdlePaused ? (
              <div className="vm-idle-notice" data-testid="voice-idle-paused">
                <h2>Conversation paused</h2>
                <p role="status">Voice Mode disconnected after inactivity. Your saved progress is still here.</p>
                {error ? <p role="alert">{error}</p> : null}
                <button type="button" className="vm-secondary-action" onClick={() => { void reconnect(); }}>
                  Resume conversation
                </button>
              </div>
            ) : <PrimaryMic
              status={runtimeStatus}
              isMuted={isMuted}
              disabled={showCrisisNotice || session?.status !== 'active' || !currentTarget}
              onMuteToggle={toggleMute}
              onReconnect={reconnect}
            />}
          </div>
        ) : null}

        {isStaff ? (
          <VoiceDebugDrawer
            open={isDebugOpen}
            onToggle={() => setIsDebugOpen((previous) => !previous)}
            sessionId={session?.id}
            sessionStatus={session?.status}
            showClearCrisisAction={session?.status === 'crisis_paused' || uiMode === 'crisis'}
            userTranscriptEntries={userTranscriptEntries}
            assistantTranscriptEntries={assistantTranscriptEntries}
            latestExtraction={latestExtraction}
            questionAnswerRows={questionAnswerRows}
            recentRealtimeEvents={recentRealtimeEvents}
            instructionBlock={instructionBlock}
            costEstimate={costEstimate}
            latencyMetrics={latencyMetrics}
            aiSafety={aiSafety}
            runtime={session?.voice_runtime}
            defaultRuntime={session?.default_runtime}
            debugRuntimeOverride={session?.debug_runtime_override}
            onSetRuntimeOverride={onRuntimeChanged ? (runtime) => { void handleSetRuntimeOverride(runtime); } : undefined}
            processingModels={session?.processing_models}
            defaultProcessingModels={session?.default_processing_models}
            processingModelOptions={session?.processing_model_options}
            processingModelsUnconfirmed={processingModelsUnconfirmed}
            processingModelsNotice={processingModelsNotice}
            previousProcessingModels={previousProcessingModels}
            onSetProcessingModelsOverride={onProcessingModelsChanged ? (models) => { void handleSetProcessingModelsOverride(models); } : undefined}
            realtimeModel={realtimeModel}
            environmentRealtimeModel={environmentRealtimeModel}
            globalRealtimeModelOverride={globalRealtimeModelOverride}
            debugRealtimeModelOverride={debugRealtimeModelOverride}
            realtimeModelOptions={realtimeModelOptions}
            transcriptionModel={transcriptionModel}
            environmentTranscriptionModel={environmentTranscriptionModel}
            globalTranscriptionModelOverride={globalTranscriptionModelOverride}
            debugTranscriptionModelOverride={debugTranscriptionModelOverride}
            transcriptionModelOptions={transcriptionModelOptions}
            debugActionBusy={debugActionBusy}
            debugActionError={debugActionError}
            onClearCrisisPause={() => setDebugConfirmAction('clear_crisis')}
            onResetRun={() => setDebugConfirmAction('reset_run')}
            onSetRealtimeModelOverride={(nextModel) => { void handleSetRealtimeModelOverride(nextModel); }}
            onSetTranscriptionModelOverride={(nextModel) => { void handleSetTranscriptionModelOverride(nextModel); }}
          />
        ) : null}

        {debugConfirmAction ? (
          <Popup
            ref={debugConfirmPopupRef}
            onClose={handleCloseDebugConfirm}
            title={DEBUG_CONFIRM_COPY[debugConfirmAction].title}
            description={DEBUG_CONFIRM_COPY[debugConfirmAction].description}
            size="default"
            actionsLayout="end"
            isSubmitting={Boolean(debugActionBusy)}
            aria-label={DEBUG_CONFIRM_COPY[debugConfirmAction].title}
            style={{ pointerEvents: 'auto' }}
            actions={(
              <div className="vm-debug-confirm-actions">
                <Button
                  type="button"
                  variant="secondary"
                  size="auto"
                  onClick={() => debugConfirmPopupRef.current?.close()}
                  disabled={Boolean(debugActionBusy)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="auto"
                  className={debugConfirmAction === 'reset_run' ? 'vm-debug-confirm-danger' : undefined}
                  onClick={handleConfirmDebugAction}
                  disabled={Boolean(debugActionBusy)}
                >
                  {debugActionBusy ? (
                    <Spinner size="sm" tone="light" />
                  ) : DEBUG_CONFIRM_COPY[debugConfirmAction].confirmLabel}
                </Button>
              </div>
            )}
          >
            {debugActionError ? <p className="vm-debug-confirm-error">{debugActionError}</p> : null}
          </Popup>
        ) : null}
      </div>
    </QuestionLayout>
    </ThemeContextProvider>
  );
};

const VoiceFlowLoader: React.FC<VoiceFlowProps & { resolvedRunId: number | null }> = (props) => {
  const [bootstrap, setBootstrap] = useState<VoiceFlowSessionBootstrap | null>(null);
  const [previousProcessingModels, setPreviousProcessingModels] = useState<VoiceProcessingModels | null>(null);
  const [processingModelsNotice, setProcessingModelsNotice] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();
  const { flowName, resolvedRunId, context } = props;

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    if (!context && resolvedRunId === null) {
      setLoadError(true);
      return;
    }
    void voiceFlowApi.createSession(flowName, context ?? (resolvedRunId as number)).then(({ data }) => {
      if (!cancelled) setBootstrap(data);
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    });
    return () => { cancelled = true; };
  }, [attempt, context, flowName, resolvedRunId]);

  useEffect(() => {
    if (!loadError || attempt >= 3) return;
    const retry = window.setTimeout(() => setAttempt((value) => value + 1), 1000 * 2 ** attempt);
    return () => window.clearTimeout(retry);
  }, [attempt, loadError]);

  useFlowThemeTransitionReady(loadError, { unthemed: true });

  if (loadError) {
    const params = new URLSearchParams({ mode: 'standard' });
    if (resolvedRunId !== null) params.set('flowRunId', String(resolvedRunId));
    if (props.targetDate) params.set('targetDate', props.targetDate);
    return <div role="alert">
      <ErrorNotice error="Unable to load Voice Mode. Your saved progress is safe."
        onReconnect={() => setAttempt((value) => value + 1)} />
      <Button onClick={() => navigate({
        pathname: typeof props.courseId === 'number'
          ? `/courses/${props.courseId}/flows/${flowName}` : `/flows/${flowName}`,
        search: params.toString(),
      })}>Standard mode</Button>
    </div>;
  }
  if (!bootstrap) return <QuestionFlowLoadingState />;
  const runtime = bootstrap.voice_runtime ?? 'realtime';
  return <VoiceFlowScreen {...props}
    key={`${bootstrap.id}:${runtime}:${bootstrap.runtime_generation ?? 0}`}
    initialVoiceBootstrap={bootstrap}
    useSession={runtime === 'live' ? useLiveVoiceSession : useVoiceSession}
    onRuntimeChanged={setBootstrap}
    previousProcessingModels={previousProcessingModels}
    processingModelsNotice={processingModelsNotice}
    onProcessingModelsChanged={(nextBootstrap, notice) => {
      setPreviousProcessingModels(notice ? null : bootstrap.processing_models ?? null);
      setProcessingModelsNotice(notice ?? null);
      setBootstrap(nextBootstrap);
    }} />;
};

export const VoiceFlow: React.FC<VoiceFlowProps> = (props) => {
  const resolvedRunId = getResolvedFlowRunId(props.initialBootstrap ?? null, props.flowRunId);
  return <VoiceFlowLoader {...props} resolvedRunId={resolvedRunId}
    key={`${props.flowName}:${props.context?.kind ?? 'flow_run'}:${props.context?.id ?? resolvedRunId}`} />;
};

export default VoiceFlow;
